import { DateTime } from 'luxon';
import { Base64 } from 'js-base64';
import jwtDecode from 'jwt-decode';
import { format } from 'd3-format';

// Override DateTime.toJSON() to always convert dates to UTC
DateTime.prototype.toJSON = function () {
    return this.toUTC().toISO();
};
const getters = (object) => {
    let attrs = [];
    while (object) {
        attrs = attrs.concat(Object.entries(Object.getOwnPropertyDescriptors(object))
            .filter(([key, descriptor]) => typeof descriptor.get === 'function' && !key.startsWith('_'))
            .map(([key]) => key));
        object = Object.getPrototypeOf(object);
    }
    return attrs;
};
class Model {
    constructor(attrs = {}) {
        this.creatableFields = [];
        this.updatableFields = [];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.constructor.init();
    }
    static all(...args) {
        throw new Error('To be implemented in repositories');
    }
    static castToBoolean(value) {
        if ('boolean' === typeof value) {
            return value;
        }
        if (value instanceof Boolean) {
            return value.valueOf();
        }
        if (['0', 'false', 'no'].includes((value + '').toLowerCase())) {
            return false;
        }
        return !!value;
    }
    static castToDate(value) {
        if (value instanceof DateTime) {
            return value;
        }
        if (value instanceof Date) {
            return DateTime.fromJSDate(value);
        }
        const valueString = String(value);
        if (/^[0-9]{10}$/.test(valueString)) {
            return DateTime.fromSeconds(+valueString);
        }
        if (/^[0-9]{13}$/.test(valueString)) {
            return DateTime.fromMillis(+valueString);
        }
        if (/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(valueString)) {
            return DateTime.fromSQL(`${value} 00:00:00 UTC`);
        }
        if (/^[0-9]{4}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2}:[0-9]{2}$/.test(valueString)) {
            return DateTime.fromSQL(`${value} UTC`);
        }
        const dt = DateTime.fromISO(valueString);
        return dt.isValid ? dt : null;
    }
    static castToFloat(value) {
        if ('number' === typeof value && !Number.isInteger(value)) {
            return value;
        }
        const result = parseFloat(value);
        return !Number.isNaN(result) ? result : null;
    }
    static castToInteger(value) {
        if (Number.isInteger(value)) {
            return value;
        }
        const result = parseInt(value, 10);
        return !Number.isNaN(result) ? result : null;
    }
    static castToString(value) {
        if (value === null || value === undefined) {
            return null;
        }
        return value + '' || null;
    }
    /**
     * @deprecated
     */
    static config() {
        return {};
    }
    static init() {
        if (this._initialized) {
            return;
        }
        this._getters = getters(this.prototype);
        this._initialized = true;
    }
    static one(...args) {
        throw new Error('To be implemented in repositories');
    }
    attrs(getters = true, recursive = false) {
        const recurse = (getters, value) => {
            if (value instanceof Array) {
                return value.map(recurse);
            }
            if (value instanceof Model) {
                return value.attrs(getters, true);
            }
            return value;
        };
        const attrs = {};
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        Object.keys(this).concat(getters ? this.constructor._getters : [])
            .forEach((attr) => {
            if (attr.startsWith('_')) {
                return;
            }
            // TODO: `recurseRemovingCreatableAndUpdatableFields` should be considered
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            attrs[attr] = recursive ? recurse(getters, this[attr]) : this[attr];
        });
        return attrs;
    }
    creatable() {
        const attrs = {};
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.constructor.creatableFields.forEach((attr) => attrs[attr] = Model.getKeyValue(attr)(this));
        return attrs;
    }
    /**
     * @deprecated Use setters
     */
    fill(data) {
        Object.getOwnPropertyNames(data).forEach((property) => {
            if (!Object.prototype.hasOwnProperty.call(this, property)) {
                return;
            }
            if (undefined === data[property] || null === data[property]) {
                Model.setKeyValue(property, null)(this);
                return;
            }
            Model.setKeyValue(property, data[property])(this);
        });
        return this;
    }
    toJSON() {
        return this.attrs();
    }
    updatable() {
        const attrs = {};
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.constructor.updatableFields.forEach((attr) => attrs[attr] = Model.getKeyValue(attr)(this));
        return attrs;
    }
}
Model._getters = [];
Model._initialized = false;
Model.getKeyValue = (key) => (obj) => obj[key];
Model.setKeyValue = (key, value) => (obj) => obj[key] = value;

class GuardRepositoryModel extends Model {
    create(...args) {
        throw new Error('To be implemented in repositories');
    }
    destroy() {
        throw new Error('To be implemented in repositories');
    }
    update() {
        throw new Error('To be implemented in repositories');
    }
}

class Assessment extends GuardRepositoryModel {
    constructor({ completed, id, progress, reassessment, started, student_id, } = {}) {
        super();
        this.completed = Assessment.castToDate(completed);
        this.id = Assessment.castToInteger(id);
        this.progress = Assessment.castToInteger(progress);
        this.reassessment = Assessment.castToBoolean(reassessment);
        this.started = Assessment.castToDate(started);
        this.student_id = Assessment.castToInteger(student_id);
    }
    getCompleted() {
        return this.completed;
    }
    setCompleted(completed) {
        this.completed = Assessment.castToDate(completed);
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = Assessment.castToInteger(id);
        return this;
    }
    getProgress() {
        return this.progress;
    }
    setProgress(progress) {
        this.progress = Assessment.castToInteger(progress);
        return this;
    }
    getReassessment() {
        return this.reassessment;
    }
    setReassessment(reassessment) {
        this.reassessment = Assessment.castToBoolean(reassessment);
        return this;
    }
    getStarted() {
        return this.started;
    }
    setStarted(started) {
        this.started = Assessment.castToDate(started);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = Assessment.castToInteger(student_id);
        return this;
    }
}

class AuthHistory extends GuardRepositoryModel {
    constructor({ authenticator, date, ip } = {}) {
        super();
        this.authenticator = authenticator !== null && authenticator !== void 0 ? authenticator : null;
        this.date = AuthHistory.castToDate(date);
        this.ip = AuthHistory.castToString(ip);
    }
    getAuthenticator() {
        return this.authenticator;
    }
    setAuthenticator(authenticator) {
        this.authenticator = authenticator !== null && authenticator !== void 0 ? authenticator : null;
        return this;
    }
    getDate() {
        return this.date;
    }
    setDate(date) {
        this.date = AuthHistory.castToDate(date);
        return this;
    }
    getIp() {
        return this.ip;
    }
    setIp(ip) {
        this.ip = AuthHistory.castToString(ip);
        return this;
    }
}

class AuthStats extends GuardRepositoryModel {
    constructor({ last_updated, online } = {}) {
        super();
        this.last_updated = AuthStats.castToDate(last_updated);
        this.online = online !== null && online !== void 0 ? online : {};
    }
    getLastUpdated() {
        return this.last_updated;
    }
    setLastUpdated(last_updated) {
        this.last_updated = AuthStats.castToDate(last_updated);
        return this;
    }
    getOnline() {
        return this.online;
    }
    setOnline(online) {
        this.online = online !== null && online !== void 0 ? online : {};
        return this;
    }
}

class Avatar extends GuardRepositoryModel {
    constructor({ attributes, student_id } = {}) {
        super();
        this.attributes = attributes !== null && attributes !== void 0 ? attributes : {};
        this.student_id = Avatar.castToInteger(student_id);
    }
    getAttributes() {
        return this.attributes;
    }
    setAttributes(attributes) {
        this.attributes = attributes !== null && attributes !== void 0 ? attributes : {};
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = Avatar.castToInteger(student_id);
        return this;
    }
}
Avatar.creatableFields = [
    'attributes',
    'student_id',
];
Avatar.updatableFields = [
    'attributes',
];

class Locale extends GuardRepositoryModel {
    constructor({ id } = {}) {
        super();
        this.id = 'en-gb';
        if (id) {
            this.id = id;
        }
    }
    static decode(id) {
        const decoded = {
            language: null,
            country: null,
        };
        if (!id || 'string' !== typeof id) {
            return decoded;
        }
        const matches = id.match(/^([a-z]{2})-([a-z]{2})$/);
        if (!matches) {
            return decoded;
        }
        [, decoded.language, decoded.country] = matches;
        return decoded;
    }
    get country() {
        return Locale.decode(this.id).country;
    }
    get language() {
        return Locale.decode(this.id).language;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        if (id) {
            this.id = id;
        }
        return this;
    }
}

class Topic extends GuardRepositoryModel {
    constructor({ id, name, locale } = {}) {
        super();
        this.locale = new Locale();
        this.id = Topic.castToString(id);
        this.name = Topic.castToString(name);
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = Topic.castToString(id);
        return this;
    }
    getLocale() {
        return this.locale;
    }
    setLocale(locale) {
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
        return this;
    }
    getName() {
        return this.name;
    }
    setName(name) {
        this.name = Topic.castToString(name);
        return this;
    }
}

const HAS_UPE_SUPPORT = (() => {
    try {
        new RegExp('\\p{Lu}', 'u');
        return true;
    }
    catch (err) {
        return false;
    }
})();
class User extends GuardRepositoryModel {
    constructor({ country, email, first_name, id, last_name, locale, partner_id, partner_managed, partner_name, partner_url, partner_user_id, sso_access_id, timezone, username, } = {}) {
        super();
        this.id = User.castToInteger(id);
        this.country = User.castToString(country);
        this.email = User.castToString(email);
        this.first_name = User.castToString(first_name);
        this.last_name = User.castToString(last_name);
        this.partner_id = User.castToString(partner_id);
        this.partner_name = User.castToString(partner_name);
        this.partner_url = User.castToString(partner_url);
        this.partner_user_id = User.castToString(partner_user_id);
        this.sso_access_id = User.castToString(sso_access_id);
        this.timezone = User.castToString(timezone);
        this.username = User.castToString(username);
        this.partner_managed = partner_managed ? User.castToBoolean(partner_managed) : false;
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
    }
    static createFromType(type, attrs = {}) {
        switch (type) {
            case 'organization_contact':
                return new OrganizationContact(attrs);
            case 'parent':
                return new Parent(attrs);
            case 'student':
                return new Student(attrs);
            case 'teacher':
                return new Teacher(attrs);
            default:
                return null;
        }
    }
    static validatePassword(password) {
        return this.PASSWORD_REGEXP.test(password);
    }
    get type() {
        switch (true) {
            case this instanceof OrganizationContact:
                return 'organization_contact';
            case this instanceof Parent:
                return 'parent';
            case this instanceof Student:
                return 'student';
            case this instanceof Teacher:
                return 'teacher';
            default:
                return null;
        }
    }
    get name() {
        return [this.first_name, this.last_name].filter((part) => !!part).join(' ') || null;
    }
    get week_starts_on() {
        switch (this.country) {
            case 'af':
            case 'dj':
            case 'ir':
                return 6; // Saturday
            case 'bh':
            case 'bd':
            case 'dz':
            case 'eg':
            case 'iq':
            case 'il':
            case 'jo':
            case 'kw':
            case 'ly':
            case 'mv':
            case 'mr':
            case 'np':
            case 'om':
            case 'ps':
            case 'qa':
            case 'sa':
            case 'sd':
            case 'sy':
            case 'ye':
                return 7; // Sunday
            default:
                return 1; // Monday
        }
    }
    startOfWeek(now = DateTime.now()) {
        if (!this.timezone) {
            return null; // Impossible to obtain the start of the week without a timezone
        }
        let startOfWeek = now.setZone(this.timezone);
        if (startOfWeek.weekday > this.week_starts_on) {
            startOfWeek = startOfWeek.minus({ days: startOfWeek.weekday - this.week_starts_on });
        }
        else if (startOfWeek.weekday < this.week_starts_on) {
            startOfWeek = startOfWeek.minus({ days: 7 - (this.week_starts_on - startOfWeek.weekday) });
        }
        return startOfWeek.startOf('day');
    }
    getCountry() {
        return this.country;
    }
    setCountry(country) {
        this.country = User.castToString(country);
        return this;
    }
    getEmail() {
        return this.email;
    }
    setEmail(email) {
        this.email = User.castToString(email);
        return this;
    }
    getFirstName() {
        return this.first_name;
    }
    setFirstName(first_name) {
        this.first_name = User.castToString(first_name);
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = User.castToInteger(id);
        return this;
    }
    getLastName() {
        return this.last_name;
    }
    setLastName(last_name) {
        this.last_name = User.castToString(last_name);
        return this;
    }
    getLocale() {
        return this.locale;
    }
    setLocale(locale) {
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
        return this;
    }
    getPartnerId() {
        return this.partner_id;
    }
    setPartnerId(partner_id) {
        this.partner_id = User.castToString(partner_id);
        return this;
    }
    getPartnerManaged() {
        return this.partner_managed;
    }
    setPartnerManaged(partner_managed) {
        this.partner_managed = partner_managed ? User.castToBoolean(partner_managed) : false;
        return this;
    }
    getPartnerName() {
        return this.partner_name;
    }
    setPartnerName(partner_name) {
        this.partner_name = User.castToString(partner_name);
        return this;
    }
    getPartnerUrl() {
        return this.partner_url;
    }
    setPartnerUrl(partner_url) {
        this.partner_url = User.castToString(partner_url);
        return this;
    }
    getPartnerUserId() {
        return this.partner_id;
    }
    setPartnerUserId(partner_id) {
        this.partner_id = User.castToString(partner_id);
        return this;
    }
    getSsoAccessId() {
        return this.sso_access_id;
    }
    setSsoAccessId(sso_access_id) {
        this.sso_access_id = User.castToString(sso_access_id);
        return this;
    }
    getTimezone() {
        return this.timezone;
    }
    setTimezone(timezone) {
        this.timezone = User.castToString(timezone);
        return this;
    }
    getUsername() {
        return this.username;
    }
    setUsername(username) {
        this.username = User.castToString(username);
        return this;
    }
}
User.PASSWORD_MIN_LENGTH = 8;
User.PASSWORD_REGEXP = HAS_UPE_SUPPORT
    // Use Unicode Property Escapes (if supported) to allow uppercase characters in any language
    ? new RegExp('^(?=.*[0-9])(?=.*\\p{Lu})(.{' + User.PASSWORD_MIN_LENGTH + ',})$', 'u')
    // Fallback to allow uppercase characters in English and Russian only
    : new RegExp('^(?=.*[0-9])(?=.*[A-ZА-Я])(.{' + User.PASSWORD_MIN_LENGTH + ',})$');
User.creatableFields = [
    'first_name',
    'last_name',
    'partner_id',
    'partner_user_id',
    'sso_access_id',
    'username',
];
User.updatableFields = [
    'first_name',
    'last_name',
    'sso_access_id',
];

class Student extends User {
    constructor({ country, email, first_name, id, last_name, locale, partner_id, partner_managed, partner_name, partner_url, partner_user_id, sso_access_id, timezone, username, active, credits, dob, gender, last_assessment, locale_alternatives, locale_preferred, owner, parent_id, parent_linkable, parent_newsletter, school_id, topic_focus, usage_cap, pupil_id, clever_id, password, } = {}) {
        super({
            country,
            email,
            first_name,
            id,
            last_name,
            locale,
            partner_id,
            partner_managed,
            partner_name,
            partner_url,
            partner_user_id,
            sso_access_id,
            timezone,
            username,
        });
        this.active = Student.castToBoolean(active);
        this.credits = Student.castToInteger(credits);
        this.dob = Student.castToString(dob);
        this.gender = Student.castToString(gender);
        this.pupil_id = Student.castToInteger(pupil_id);
        this.clever_id = Student.castToString(clever_id);
        this.password = Student.castToString(password);
        //Todo: What can last_assessment be if not an assessment
        if (last_assessment) {
            this.last_assessment = last_assessment instanceof Assessment ? last_assessment : new Assessment(last_assessment);
        }
        else {
            this.last_assessment = null;
        }
        this.locale_alternatives = locale_alternatives
            ? locale_alternatives.map((locale) => new Locale(locale))
            : [];
        this.owner = Student.castToString(owner);
        this.parent_id = Student.castToInteger(parent_id);
        this.parent_linkable = Student.castToBoolean(parent_linkable);
        this.parent_newsletter = Student.castToBoolean(parent_newsletter);
        this.school_id = Student.castToInteger(school_id);
        this.usage_cap = Student.castToInteger(usage_cap);
        if (topic_focus) {
            this.topic_focus = topic_focus instanceof Topic ? topic_focus : new Topic(topic_focus);
        }
        else {
            this.topic_focus = null;
        }
        if (locale_preferred) {
            this.locale_preferred = locale_preferred instanceof Locale ? locale_preferred : new Locale(locale_preferred);
        }
        else {
            this.locale_preferred = new Locale();
        }
    }
    get assessed() {
        return !!(this.last_assessment && this.last_assessment.completed);
    }
    getActive() {
        return this.active;
    }
    setActive(active) {
        this.active = Student.castToBoolean(active);
        return this;
    }
    getCredits() {
        return this.credits;
    }
    setCredits(credits) {
        this.credits = Student.castToInteger(credits);
        return this;
    }
    getDob() {
        return this.dob;
    }
    setDob(dob) {
        this.dob = Student.castToString(dob);
        return this;
    }
    getGender() {
        return this.gender;
    }
    setGender(gender) {
        this.gender = Student.castToString(gender);
        return this;
    }
    getLastAssessment() {
        return this.last_assessment;
    }
    setLastAssessment(last_assessment) {
        if (last_assessment) {
            this.last_assessment = last_assessment instanceof Assessment ? last_assessment : new Assessment(last_assessment);
        }
        else {
            this.last_assessment = null;
        }
        return this;
    }
    getLocaleAlternatives() {
        return this.locale_alternatives;
    }
    setLocaleAlternatives(locale_alternatives) {
        this.locale_alternatives = locale_alternatives
            ? locale_alternatives.map((locale) => new Locale(locale))
            : [];
        return this;
    }
    getLocalePreferred() {
        return this.locale_preferred;
    }
    setLocalePreferred(locale_preferred) {
        if (locale_preferred) {
            this.locale_preferred = locale_preferred instanceof Locale ? locale_preferred : new Locale(locale_preferred);
        }
        else {
            this.locale_preferred = new Locale();
        }
        return this;
    }
    getOwner() {
        return this.owner;
    }
    setOwner(owner) {
        this.owner = Student.castToString(owner);
        return this;
    }
    getParentId() {
        return this.parent_id;
    }
    setParentId(parent_id) {
        this.parent_id = Student.castToInteger(parent_id);
        return this;
    }
    getParentLinkable() {
        return this.parent_linkable;
    }
    setParentLinkable(parent_linkable) {
        this.parent_linkable = Student.castToBoolean(parent_linkable);
        return this;
    }
    getParentNewsletter() {
        return this.parent_newsletter;
    }
    setParentNewsletter(parent_newsletter) {
        this.parent_newsletter = Student.castToBoolean(parent_newsletter);
        return this;
    }
    getSchoolId() {
        return this.school_id;
    }
    setSchoolId(school_id) {
        this.school_id = Student.castToInteger(school_id);
        return this;
    }
    getTopicFocus() {
        return this.topic_focus;
    }
    setTopicFocus(topic_focus) {
        if (topic_focus) {
            this.topic_focus = topic_focus instanceof Topic ? topic_focus : new Topic(topic_focus);
        }
        else {
            this.topic_focus = null;
        }
        return this;
    }
    getUsageCap() {
        return this.usage_cap;
    }
    setUsageCap(usage_cap) {
        this.usage_cap = Student.castToInteger(usage_cap);
        return this;
    }
    getPupilId() {
        return this.pupil_id;
    }
    setPupilId(pupil_id) {
        this.pupil_id = Student.castToInteger(pupil_id);
        return this;
    }
    getCleverId() {
        return this.clever_id;
    }
    setCleverId(clever_id) {
        this.clever_id = Student.castToString(clever_id);
        return this;
    }
    getPassword() {
        return this.password;
    }
    setPassword(password) {
        this.password = Student.castToString(password);
        return this;
    }
}
Student.PASSWORD_MAX_LENGTH = 20;
Student.PASSWORD_MIN_LENGTH = 5;
Student.PASSWORD_REGEXP = new RegExp(`^.{${Student.PASSWORD_MIN_LENGTH},${Student.PASSWORD_MAX_LENGTH}}$`);
Student.creatableFields = [
    'dob',
    'gender',
    'locale_preferred',
    'owner',
    'parent_id',
    'school_id',
    'usage_cap',
    'pupil_id',
    'clever_id',
    'password',
].concat(User.creatableFields);
Student.updatableFields = [
    'credits',
    'dob',
    'gender',
    'locale_preferred',
    'owner',
    'parent_id',
    'usage_cap',
    'pupil_id',
    'clever_id',
    'password',
].concat(User.updatableFields);

class Buddy extends GuardRepositoryModel {
    constructor({ avatar, buddy, status, student_id } = {}) {
        super();
        if (avatar) {
            this.avatar = avatar instanceof Avatar ? avatar : new Avatar(avatar);
        }
        else {
            this.avatar = new Avatar();
        }
        if (buddy) {
            this.buddy = buddy instanceof Student ? buddy : new Student(buddy);
        }
        else {
            this.buddy = new Student();
        }
        this.status = Buddy.castToString(status);
        this.student_id = Buddy.castToInteger(student_id);
    }
    getAvatar() {
        return this.avatar;
    }
    setAvatar(avatar) {
        if (avatar) {
            this.avatar = avatar instanceof Avatar ? avatar : new Avatar(avatar);
        }
        else {
            this.avatar = new Avatar();
        }
        return this;
    }
    getBuddy() {
        return this.buddy;
    }
    setBuddy(buddy) {
        if (buddy) {
            this.buddy = buddy instanceof Student ? buddy : new Student(buddy);
        }
        else {
            this.buddy = new Student();
        }
        return this;
    }
    getStatus() {
        return this.status;
    }
    setStatus(status) {
        this.status = Buddy.castToString(status);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = Buddy.castToInteger(student_id);
        return this;
    }
}
Buddy.creatableFields = ['buddy'];
Buddy.updatableFields = ['status'];

class TimeTaken extends GuardRepositoryModel {
    constructor({ lesson, tutorial, total } = {}) {
        var _a, _b, _c;
        super();
        this.lesson = (_a = TimeTaken.castToInteger(lesson)) !== null && _a !== void 0 ? _a : 0;
        this.tutorial = (_b = TimeTaken.castToInteger(tutorial)) !== null && _b !== void 0 ? _b : 0;
        this.total = (_c = TimeTaken.castToInteger(total)) !== null && _c !== void 0 ? _c : 0;
    }
    getLesson() {
        return this.lesson;
    }
    setLesson(lesson) {
        var _a;
        this.lesson = (_a = TimeTaken.castToInteger(lesson)) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
    getTutorial() {
        return this.tutorial;
    }
    setTutorial(tutorial) {
        var _a;
        this.tutorial = (_a = TimeTaken.castToInteger(tutorial)) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
    getTotal() {
        return this.total;
    }
    setTotal(total) {
        var _a;
        this.total = (_a = TimeTaken.castToInteger(total)) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
}

class LeaderboardEntry extends GuardRepositoryModel {
    constructor({ avatar, credits, credits_this_week, rank, student, time_taken, score, } = {}) {
        super();
        if (avatar) {
            this.avatar = avatar instanceof Avatar ? avatar : new Avatar(avatar);
        }
        else {
            this.avatar = new Avatar();
        }
        this.credits = LeaderboardEntry.castToInteger(credits);
        this.credits_this_week = LeaderboardEntry.castToInteger(credits_this_week);
        this.rank = LeaderboardEntry.castToInteger(rank);
        if (student) {
            this.student = student instanceof Student ? student : new Student(student);
        }
        else {
            this.student = new Student();
        }
        if (time_taken) {
            this.time_taken = time_taken instanceof TimeTaken ? time_taken : new TimeTaken(time_taken);
        }
        else {
            this.time_taken = new TimeTaken();
        }
        this.score = LeaderboardEntry.castToInteger(score);
    }
    getAvatar() {
        return this.avatar;
    }
    setAvatar(avatar) {
        if (avatar) {
            this.avatar = avatar instanceof Avatar ? avatar : new Avatar(avatar);
        }
        else {
            this.avatar = new Avatar();
        }
        return this;
    }
    getCredits() {
        return this.credits;
    }
    setCredits(credits) {
        this.credits = LeaderboardEntry.castToInteger(credits);
        return this;
    }
    getCreditsThisWeek() {
        return this.credits_this_week;
    }
    setCreditsThisWeek(credits_this_week) {
        this.credits_this_week = LeaderboardEntry.castToInteger(credits_this_week);
        return this;
    }
    getRank() {
        return this.rank;
    }
    setRank(rank) {
        this.rank = LeaderboardEntry.castToInteger(rank);
        return this;
    }
    getStudent() {
        return this.student;
    }
    setStudent(student) {
        if (student) {
            this.student = student instanceof Student ? student : new Student(student);
        }
        else {
            this.student = new Student();
        }
        return this;
    }
    getTimeTaken() {
        return this.time_taken;
    }
    setTimeTaken(time_taken) {
        if (time_taken) {
            this.time_taken = time_taken instanceof TimeTaken ? time_taken : new TimeTaken(time_taken);
        }
        else {
            this.time_taken = new TimeTaken();
        }
        return this;
    }
    getScore() {
        return this.score;
    }
    setScore(score) {
        this.score = LeaderboardEntry.castToInteger(score);
        return this;
    }
}

class Leaderboard extends GuardRepositoryModel {
    constructor({ entries } = {}) {
        super();
        this.entries = [];
        if (entries) {
            this.entries = entries.map((entry) => new LeaderboardEntry(entry));
        }
    }
    getEntries() {
        return this.entries;
    }
    setEntries(entries) {
        if (entries) {
            this.entries = entries.map((entry) => new LeaderboardEntry(entry));
        }
        return this;
    }
}

class BuddyLeaderboard extends Leaderboard {
    constructor({ entries, student_id } = {}) {
        super({ entries });
        this.student_id = BuddyLeaderboard.castToInteger(student_id);
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = BuddyLeaderboard.castToInteger(student_id);
        return this;
    }
}

class ChallengeStats extends GuardRepositoryModel {
    constructor({ total, wins } = {}) {
        super();
        this.total = ChallengeStats.castToInteger(total);
        this.wins = ChallengeStats.castToInteger(wins);
    }
    get win_rate() {
        if (!this.total) {
            return 0;
        }
        return Math.floor(this.wins / this.total * 100);
    }
    getTotal() {
        return this.total;
    }
    setTotal(total) {
        this.total = ChallengeStats.castToInteger(total);
        return this;
    }
    getWins() {
        return this.wins;
    }
    setWins(wins) {
        this.wins = ChallengeStats.castToInteger(wins);
        return this;
    }
}

const cdnBaseUrl = (locale) => {
    if (!locale || 'en-ae' === locale.id) {
        locale = new Locale();
    }
    return `https://${process.env.HOSTNAME_CDN || process.env.REACT_APP_HOSTNAME_CDN}/${locale.country}/${locale.id}`;
};
class Lesson extends GuardRepositoryModel {
    constructor({ has_test, id, locale, target_time, title, topic } = {}) {
        super();
        this.has_test = Lesson.castToBoolean(has_test);
        this.id = Lesson.castToString(id);
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
        this.target_time = Lesson.castToInteger(target_time);
        this.title = Lesson.castToString(title);
        if (topic) {
            this.topic = topic instanceof Topic ? topic : new Topic(topic);
        }
        else {
            this.topic = new Topic();
        }
    }
    get age() {
        return Lesson.decode(this.id).age;
    }
    get level() {
        return Lesson.decode(this.id).level;
    }
    get test() {
        return 'p' === Lesson.decode(this.id).type;
    }
    get thumbnail() {
        const filename = this.id ? `${this.id.substring(0, 13)}x${this.id.substring(14)}` : 'no-image';
        return `${cdnBaseUrl(this.locale)}/maths/tutor/images/exercises/${filename}.jpg`;
    }
    get url() {
        return `${cdnBaseUrl(this.locale)}/v2/maths/lessons/${this.test ? 'pb' : 'ex'}/${this.id}.swf`;
    }
    get player_url() {
        return `https://${process.env.HOSTNAME_LESSON_PLAYER || process.env.REACT_APP_HOSTNAME_LESSON_PLAYER}/${this.id}?locale=${this.locale.id}`;
    }
    static decode(id) {
        const decoded = {
            age: null,
            level: null,
            type: null,
            topic: null,
        };
        if (!id || 'string' !== typeof id) {
            return decoded;
        }
        const matches = id.match(/^MA_GBR_([0-9]{4})([A-Z]{2})([a-z])([0-9]{4})$/);
        if (!matches) {
            return decoded;
        }
        let age, level;
        [, age, decoded.topic, decoded.type, level] = matches;
        decoded.age = parseInt(age, 10);
        decoded.level = parseInt(level, 10);
        return decoded;
    }
    getHasTest() {
        return this.has_test;
    }
    setHasTest(has_test) {
        this.has_test = Lesson.castToBoolean(has_test);
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = Lesson.castToString(id);
        return this;
    }
    getLocale() {
        return this.locale;
    }
    setLocale(locale) {
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
        return this;
    }
    getTargetTime() {
        return this.target_time;
    }
    setTargetTime(target_time) {
        this.target_time = Lesson.castToInteger(target_time);
        return this;
    }
    getTitle() {
        return this.title;
    }
    setTitle(title) {
        this.title = Lesson.castToString(title);
        return this;
    }
    getTopic() {
        return this.topic;
    }
    setTopic(topic) {
        if (topic) {
            this.topic = topic instanceof Topic ? topic : new Topic(topic);
        }
        else {
            this.topic = new Topic();
        }
        return this;
    }
}

class Challenge extends GuardRepositoryModel {
    constructor({ challenger, challenger_avatar, challenger_stats, lesson, opponent, opponent_avatar, opponent_stats, outcome, sent, status, } = {}) {
        super();
        if (challenger) {
            this.challenger = challenger instanceof Student ? challenger : new Student(challenger);
        }
        else {
            this.challenger = new Student();
        }
        if (challenger_avatar) {
            this.challenger_avatar = challenger_avatar instanceof Avatar ? challenger_avatar : new Avatar(challenger_avatar);
        }
        else {
            this.challenger_avatar = new Avatar();
        }
        if (challenger_stats) {
            this.challenger_stats = challenger_stats instanceof ChallengeStats ? challenger_stats : new ChallengeStats(challenger_stats);
        }
        else {
            this.challenger_stats = new ChallengeStats();
        }
        if (lesson) {
            this.lesson = lesson instanceof Lesson ? lesson : new Lesson(lesson);
        }
        else {
            this.lesson = new Lesson();
        }
        if (opponent) {
            this.opponent = opponent instanceof Student ? opponent : new Student(opponent);
        }
        else {
            this.opponent = new Student();
        }
        if (opponent_avatar) {
            this.opponent_avatar = opponent_avatar instanceof Avatar ? opponent_avatar : new Avatar(opponent_avatar);
        }
        else {
            this.opponent_avatar = new Avatar();
        }
        if (opponent_stats) {
            this.opponent_stats = opponent_stats instanceof ChallengeStats ? opponent_stats : new ChallengeStats(opponent_stats);
        }
        else {
            this.opponent_stats = new ChallengeStats();
        }
        this.outcome = Challenge.castToString(outcome);
        this.sent = Challenge.castToDate(sent);
        this.status = Challenge.castToString(status);
    }
    getChallenger() {
        return this.challenger;
    }
    setChallenger(challenger) {
        if (challenger) {
            this.challenger = challenger instanceof Student ? challenger : new Student(challenger);
        }
        else {
            this.challenger = new Student();
        }
        return this;
    }
    getChallengerAvatar() {
        return this.challenger_avatar;
    }
    setChallengerAvatar(challenger_avatar) {
        if (challenger_avatar) {
            this.challenger_avatar = challenger_avatar instanceof Avatar ? challenger_avatar : new Avatar(challenger_avatar);
        }
        else {
            this.challenger_avatar = new Avatar();
        }
        return this;
    }
    getChallengerStats() {
        return this.challenger_stats;
    }
    setChallengerStats(challenger_stats) {
        if (challenger_stats) {
            this.challenger_stats = challenger_stats instanceof ChallengeStats ? challenger_stats : new ChallengeStats(challenger_stats);
        }
        else {
            this.challenger_stats = new ChallengeStats();
        }
        return this;
    }
    getLesson() {
        return this.lesson;
    }
    setLesson(lesson) {
        if (lesson) {
            this.lesson = lesson instanceof Lesson ? lesson : new Lesson(lesson);
        }
        else {
            this.lesson = new Lesson();
        }
        return this;
    }
    getOpponent() {
        return this.opponent;
    }
    setOpponent(opponent) {
        if (opponent) {
            this.opponent = opponent instanceof Student ? opponent : new Student(opponent);
        }
        else {
            this.opponent = new Student();
        }
        return this;
    }
    getOpponentAvatar() {
        return this.opponent_avatar;
    }
    setOpponentAvatar(opponent_avatar) {
        if (opponent_avatar) {
            this.opponent_avatar = opponent_avatar instanceof Avatar ? opponent_avatar : new Avatar(opponent_avatar);
        }
        else {
            this.opponent_avatar = new Avatar();
        }
        return this;
    }
    getOpponentStats() {
        return this.opponent_stats;
    }
    setOpponentStats(opponent_stats) {
        if (opponent_stats) {
            this.opponent_stats = opponent_stats instanceof ChallengeStats ? opponent_stats : new ChallengeStats(opponent_stats);
        }
        else {
            this.opponent_stats = new ChallengeStats();
        }
        return this;
    }
    getOutcome() {
        return this.outcome;
    }
    setOutcome(outcome) {
        this.outcome = Challenge.castToString(outcome);
        return this;
    }
    getSent() {
        return this.sent;
    }
    setSent(sent) {
        this.sent = Challenge.castToDate(sent);
        return this;
    }
    getStatus() {
        return this.status;
    }
    setStatus(status) {
        this.status = Challenge.castToString(status);
        return this;
    }
}

class PaymentMethod extends GuardRepositoryModel {
    constructor({ user } = {}) {
        super();
        this.user = user && ((user instanceof User) ? user : User.createFromType(user.type, { id: user.id }));
    }
    get type() {
        switch (true) {
            case this instanceof CreditCard:
                return 'card';
            default:
                return null;
        }
    }
    static createFromType(type, attrs) {
        switch (type) {
            case 'card':
                return new CreditCard(attrs);
            default:
                return null;
        }
    }
    getUser() {
        return this.user;
    }
    setUser(user) {
        this.user = user && ((user instanceof User) ?
            user :
            User.createFromType(user.type, { id: user.id }));
        return this;
    }
}

class CreditCard extends PaymentMethod {
    constructor({ user, brand, expiry_month, expiry_year, last4, } = {}) {
        super({ user });
        this.brand = CreditCard.castToString(brand);
        this.expiry_month = CreditCard.castToInteger(expiry_month);
        this.expiry_year = CreditCard.castToInteger(expiry_year);
        this.last4 = CreditCard.castToString(last4);
    }
    getBrand() {
        return this.brand;
    }
    setBrand(brand) {
        this.brand = CreditCard.castToString(brand);
        return this;
    }
    getExpiryMonth() {
        return this.expiry_month;
    }
    setExpiryMonth(expiry_month) {
        this.expiry_month = CreditCard.castToInteger(expiry_month);
        return this;
    }
    getExpiryYear() {
        return this.expiry_year;
    }
    setExpiryYear(expiry_year) {
        this.expiry_year = CreditCard.castToInteger(expiry_year);
        return this;
    }
    getLast4() {
        return this.last4;
    }
    setLast4(last4) {
        this.last4 = CreditCard.castToString(last4);
        return this;
    }
}

class Feature extends GuardRepositoryModel {
    constructor({ id, student_id, metadata } = {}) {
        super();
        this.id = Feature.castToString(id);
        this.student_id = Feature.castToInteger(student_id);
        this.metadata = metadata;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = Feature.castToString(id);
        return this;
    }
    getMetadata() {
        return this.metadata;
    }
    setMetadata(metadata) {
        this.metadata = metadata;
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = Feature.castToInteger(student_id);
        return this;
    }
}
Feature.creatableFields = [
    'id',
    'metadata',
];
Feature.updatableFields = ['metadata'];

class Feedback extends GuardRepositoryModel {
    constructor({ created, message, student_id } = {}) {
        super();
        this.message = Feedback.castToString(message);
        this.created = Feedback.castToDate(created);
        this.student_id = Feedback.castToInteger(student_id);
    }
    getCreated() {
        return this.created;
    }
    setCreated(created) {
        this.created = Feedback.castToDate(created);
        return this;
    }
    getMessage() {
        return this.message;
    }
    setMessage(message) {
        this.message = Feedback.castToString(message);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = Feedback.castToInteger(student_id);
        return this;
    }
}
Feedback.creatableFields = [
    'created',
    'message',
];

class GlobalLeaderboard extends Leaderboard {
    constructor({ country, locale, entries } = {}) {
        super({ entries });
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
        if (country) {
            this._country = country;
        }
    }
    get country() {
        if (this._country) {
            return this._country;
        }
        return this.locale.country;
    }
    setCountry(country) {
        if (country) {
            this._country = country;
        }
        return this;
    }
    getLocale() {
        return this.locale;
    }
    setLocale(locale) {
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
        return this;
    }
}

class Identity extends GuardRepositoryModel {
    constructor({ token, internal } = { internal: false }) {
        super();
        this.internal = internal;
        this.token = Identity.castToString(token);
    }
    get claims() {
        try {
            return jwtDecode(this.token);
        }
        catch (err) {
            return {};
        }
    }
    get user() {
        const claims = this.claims;
        let sub = '';
        let usr = null;
        if (Object.getOwnPropertyNames(claims).filter((property) => property === 'sub')) {
            sub = claims.sub;
        }
        if (null == sub) {
            sub = '';
        }
        const userType = sub.split('/')[0];
        if ('system_user' === userType) {
            return null;
        }
        if (Object.getOwnPropertyNames(claims).filter((property) => property === 'usr')) {
            usr = claims.usr;
        }
        const attrs = usr && typeof usr === 'string' ? JSON.parse(Base64.decode(usr)) : {};
        return User.createFromType(userType, attrs);
    }
    get systemUser() {
        const claims = this.claims;
        let sub = '';
        let usr = null;
        if (Object.getOwnPropertyNames(claims).filter((property) => property === 'sub')) {
            sub = claims.sub;
        }
        if (null == sub) {
            sub = '';
        }
        const userType = sub.split('/')[0];
        if ('system_user' !== userType) {
            return null;
        }
        if (Object.getOwnPropertyNames(claims).filter((property) => property === 'usr')) {
            usr = claims.usr;
        }
        const attrs = usr && typeof usr === 'string' ? JSON.parse(Base64.decode(usr)) : {};
        return {
            name: attrs.name,
            code: attrs.code,
        };
    }
    getToken() {
        return this.token;
    }
    setToken(token) {
        this.token = Identity.castToString(token);
        return this;
    }
    getInternal() {
        return Identity.castToBoolean(this.internal);
    }
    setInternal(internal) {
        this.internal = Identity.castToBoolean(internal);
        return this;
    }
}

class Item extends GuardRepositoryModel {
    constructor({ display, id, last_interaction, price, purchased, student_id, catalogue_metadata, } = {}) {
        super();
        this.catalogue_metadata = catalogue_metadata !== null && catalogue_metadata !== void 0 ? catalogue_metadata : {};
        this.display = display;
        this.id = Item.castToString(id);
        this.last_interaction = Item.castToDate(last_interaction);
        this.price = Item.castToInteger(price);
        this.purchased = Item.castToDate(purchased);
        this.student_id = Item.castToInteger(student_id);
    }
    getCatalogueMetadata() {
        return this.catalogue_metadata;
    }
    setCatalogueMetadata(catalogue_metadata) {
        this.catalogue_metadata = catalogue_metadata !== null && catalogue_metadata !== void 0 ? catalogue_metadata : {};
        return this;
    }
    getDisplay() {
        return this.display;
    }
    setDisplay(display) {
        this.display = display;
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = Item.castToString(id);
        return this;
    }
    getLastInteraction() {
        return this.last_interaction;
    }
    setLastInteraction(last_interaction) {
        this.last_interaction = Item.castToDate(last_interaction);
        return this;
    }
    getPrice() {
        return this.price;
    }
    setPrice(price) {
        this.price = Item.castToInteger(price);
        return this;
    }
    getPurchased() {
        return this.purchased;
    }
    setPurchased(purchased) {
        this.purchased = Item.castToDate(purchased);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = Item.castToInteger(student_id);
        return this;
    }
}
Item.creatableFields = ['catalogue_metadata', 'display', 'price'];
Item.updatableFields = ['catalogue_metadata', 'display', 'last_interaction'];

class LessonFeedback extends GuardRepositoryModel {
    constructor({ created, description, id, lesson, questions_current, questions_total, student_id, user_agent, } = {}) {
        super();
        this.created = LessonFeedback.castToDate(created);
        this.description = LessonFeedback.castToString(description);
        this.id = LessonFeedback.castToInteger(id);
        if (lesson) {
            this.lesson = lesson instanceof Lesson ? lesson : new Lesson(lesson);
        }
        else {
            this.lesson = new Lesson();
        }
        this.questions_current = LessonFeedback.castToInteger(questions_current);
        this.questions_total = LessonFeedback.castToInteger(questions_total);
        this.student_id = LessonFeedback.castToInteger(student_id);
        this.user_agent = LessonFeedback.castToString(user_agent);
    }
    getCreated() {
        return this.created;
    }
    setCreated(created) {
        this.created = LessonFeedback.castToDate(created);
        return this;
    }
    getDescription() {
        return this.description;
    }
    setDescription(description) {
        this.description = LessonFeedback.castToString(description);
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = LessonFeedback.castToInteger(id);
        return this;
    }
    getLesson() {
        return this.lesson;
    }
    setLesson(lesson) {
        if (lesson) {
            this.lesson = lesson instanceof Lesson ? lesson : new Lesson(lesson);
        }
        else {
            this.lesson = new Lesson();
        }
        return this;
    }
    getQuestionsCurrent() {
        return this.questions_current;
    }
    setQuestionsCurrent(questions_current) {
        this.questions_current = LessonFeedback.castToInteger(questions_current);
        return this;
    }
    getQuestionsTotal() {
        return this.questions_total;
    }
    setQuestionsTotal(questions_total) {
        this.questions_total = LessonFeedback.castToInteger(questions_total);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = LessonFeedback.castToInteger(student_id);
        return this;
    }
    getUserAgent() {
        return this.user_agent;
    }
    setUserAgent(user_agent) {
        this.user_agent = LessonFeedback.castToString(user_agent);
        return this;
    }
}
LessonFeedback.creatableFields = [
    'created',
    'description',
    'lesson',
    'questions_current',
    'questions_total',
    'user_agent',
];

class LessonLeaderboard extends Leaderboard {
    constructor({ lesson_id, student_id, entries } = {}) {
        super({ entries });
        this.lesson_id = LessonLeaderboard.castToString(lesson_id);
        this.student_id = LessonLeaderboard.castToInteger(student_id);
    }
    getLessonId() {
        return this.lesson_id;
    }
    setLessonId(lesson_id) {
        this.lesson_id = LessonLeaderboard.castToString(lesson_id);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = LessonLeaderboard.castToInteger(student_id);
        return this;
    }
}

class LessonLeaderboardEntry extends GuardRepositoryModel {
    constructor({ avatar, rank, score, student, time_taken } = {}) {
        super();
        if (avatar) {
            this.avatar = avatar instanceof Avatar ? avatar : new Avatar(avatar);
        }
        else {
            this.avatar = new Avatar();
        }
        this.rank = LessonLeaderboardEntry.castToInteger(rank);
        this.score = LessonLeaderboardEntry.castToInteger(score);
        if (student) {
            this.student = student instanceof Student ? student : new Student(student);
        }
        else {
            this.student = new Student();
        }
        if (time_taken) {
            this.time_taken = time_taken instanceof TimeTaken ? time_taken : new TimeTaken(time_taken);
        }
        else {
            this.time_taken = new TimeTaken();
        }
    }
    getAvatar() {
        return this.avatar;
    }
    setAvatar(avatar) {
        if (avatar) {
            this.avatar = avatar instanceof Avatar ? avatar : new Avatar(avatar);
        }
        else {
            this.avatar = new Avatar();
        }
        return this;
    }
    getRank() {
        return this.rank;
    }
    setRank(rank) {
        this.rank = LessonLeaderboardEntry.castToInteger(rank);
        return this;
    }
    getScore() {
        return this.score;
    }
    setScore(score) {
        this.score = LessonLeaderboardEntry.castToInteger(score);
        return this;
    }
    getStudent() {
        return this.student;
    }
    setStudent(student) {
        if (student) {
            this.student = student instanceof Student ? student : new Student(student);
        }
        else {
            this.student = new Student();
        }
        return this;
    }
    getTimeTaken() {
        return this.time_taken;
    }
    setTimeTaken(time_taken) {
        if (time_taken) {
            this.time_taken = time_taken instanceof TimeTaken ? time_taken : new TimeTaken(time_taken);
        }
        else {
            this.time_taken = new TimeTaken();
        }
        return this;
    }
}

class LessonResult extends GuardRepositoryModel {
    constructor({ completed, created, credits, help1, help2, help3, id, incomplete_id, incomplete_started, lesson, mode, outcome, progression, questions_answered, questions_total, score, student_id, time_taken, completed_flag, legacy, mode_flag, stackDepth, detailedTimestamp, } = {}) {
        var _a, _b;
        super();
        this.completed = LessonResult.castToDate(completed);
        this.completed_flag = (_a = LessonResult.castToInteger(completed_flag)) !== null && _a !== void 0 ? _a : LessonResult.COMPLETED_FLAG_NONE;
        this.created = LessonResult.castToDate(created);
        this.credits = LessonResult.castToInteger(credits);
        this.help1 = LessonResult.castToInteger(help1);
        this.help2 = LessonResult.castToInteger(help2);
        this.help3 = LessonResult.castToInteger(help3);
        this.id = LessonResult.castToInteger(id);
        this.incomplete_id = LessonResult.castToInteger(incomplete_id);
        this.incomplete_started = LessonResult.castToDate(incomplete_started);
        this.legacy = legacy !== null && legacy !== void 0 ? legacy : {};
        this.detailedTimestamp = LessonResult.castToInteger(detailedTimestamp);
        if (lesson) {
            this.lesson = lesson instanceof Lesson ? lesson : new Lesson(lesson);
        }
        else {
            this.lesson = new Lesson();
        }
        const modeString = LessonResult.castToString(mode);
        this.mode = modeString ? modeString : null;
        this.mode_flag = (_b = LessonResult.castToInteger(mode_flag)) !== null && _b !== void 0 ? _b : LessonResult.MODE_FLAG_NONE;
        this.outcome = LessonResult.castToString(outcome);
        this.progression = LessonResult.castToBoolean(progression);
        this.questions_answered = LessonResult.castToInteger(questions_answered);
        this.questions_total = LessonResult.castToInteger(questions_total);
        this.score = LessonResult.castToInteger(score);
        this.student_id = LessonResult.castToInteger(student_id);
        if (time_taken) {
            this.time_taken = time_taken instanceof TimeTaken ? time_taken : new TimeTaken(time_taken);
        }
        else {
            this.time_taken = new TimeTaken();
        }
        this.stackDepth = LessonResult.castToInteger(stackDepth);
    }
    get incomplete() {
        return !this.completed;
    }
    get medal() {
        if (this.incomplete
            || LessonResult.COMPLETED_FLAG_NONE !== this.completed_flag
            || (this.lesson && this.lesson.test)) {
            return null;
        }
        if (100 === this.score) {
            return (this.lesson && this.time_taken.lesson <= this.lesson.target_time) ? 'gold' : 'silver';
        }
        return this.score >= 80 ? 'bronze' : null;
    }
    getCompleted() {
        return this.completed;
    }
    setCompleted(completed) {
        this.completed = LessonResult.castToDate(completed);
        return this;
    }
    getCompletedFlag() {
        return this.completed_flag;
    }
    setCompletedFlag(completed_flag) {
        var _a;
        this.completed_flag = (_a = LessonResult.castToInteger(completed_flag)) !== null && _a !== void 0 ? _a : LessonResult.COMPLETED_FLAG_NONE;
        return this;
    }
    getCreated() {
        return this.created;
    }
    setCreated(created) {
        this.created = LessonResult.castToDate(created);
        return this;
    }
    getCredits() {
        return this.credits;
    }
    setCredits(credits) {
        this.credits = LessonResult.castToInteger(credits);
        return this;
    }
    getHelp1() {
        return this.help1;
    }
    setHelp1(help1) {
        this.help1 = LessonResult.castToInteger(help1);
        return this;
    }
    getHelp2() {
        return this.help2;
    }
    setHelp2(help2) {
        this.help2 = LessonResult.castToInteger(help2);
        return this;
    }
    getHelp3() {
        return this.help3;
    }
    setHelp3(help3) {
        this.help3 = LessonResult.castToInteger(help3);
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = LessonResult.castToInteger(id);
        return this;
    }
    getIncompleteId() {
        return this.incomplete_id;
    }
    setIncompleteId(incomplete_id) {
        this.incomplete_id = LessonResult.castToInteger(incomplete_id);
        return this;
    }
    getIncompleteStarted() {
        return this.incomplete_started;
    }
    setIncompleteStarted(incomplete_started) {
        this.incomplete_started = LessonResult.castToDate(incomplete_started);
        return this;
    }
    getLegacy() {
        return this.legacy;
    }
    setLegacy(legacy) {
        this.legacy = legacy !== null && legacy !== void 0 ? legacy : {};
        return this;
    }
    getLesson() {
        return this.lesson;
    }
    setLesson(lesson) {
        if (lesson) {
            this.lesson = lesson instanceof Lesson ? lesson : new Lesson(lesson);
        }
        else {
            this.lesson = new Lesson();
        }
        return this;
    }
    getMode() {
        return this.mode;
    }
    setMode(mode) {
        const modeString = LessonResult.castToString(mode);
        this.mode = modeString ? modeString : null;
        return this;
    }
    getModeFlag() {
        return this.mode_flag;
    }
    setModeFlag(mode_flag) {
        var _a;
        this.mode_flag = (_a = LessonResult.castToInteger(mode_flag)) !== null && _a !== void 0 ? _a : LessonResult.MODE_FLAG_NONE;
        return this;
    }
    getOutcome() {
        return this.outcome;
    }
    setOutcome(outcome) {
        this.outcome = LessonResult.castToString(outcome);
        return this;
    }
    getProgression() {
        return this.progression;
    }
    setProgression(progression) {
        this.progression = LessonResult.castToBoolean(progression);
        return this;
    }
    getQuestionsAnswered() {
        return this.questions_answered;
    }
    setQuestionsAnswered(questions_answered) {
        this.questions_answered = LessonResult.castToInteger(questions_answered);
        return this;
    }
    getQuestionsTotal() {
        return this.questions_total;
    }
    setQuestionsTotal(questions_total) {
        this.questions_total = LessonResult.castToInteger(questions_total);
        return this;
    }
    getScore() {
        return this.score;
    }
    setScore(score) {
        this.score = LessonResult.castToInteger(score);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = LessonResult.castToInteger(student_id);
        return this;
    }
    setTimeTaken(time_taken) {
        if (time_taken) {
            this.time_taken = time_taken instanceof TimeTaken ? time_taken : new TimeTaken(time_taken);
        }
        else {
            this.time_taken = new TimeTaken();
        }
        return this;
    }
    getTimeTaken() {
        return this.time_taken;
    }
    getStackDepth() {
        return this.stackDepth;
    }
    setStackDepth(stackDepth) {
        this.stackDepth = LessonResult.castToInteger(stackDepth);
        return this;
    }
    getDetailedTimestamp() {
        return this.detailedTimestamp;
    }
    setDetailedTimestamp(detailedTimestamp) {
        this.detailedTimestamp = LessonResult.castToInteger(detailedTimestamp);
        return this;
    }
}
LessonResult.COMPLETED_FLAG_JUMPED_AHEAD = 1;
LessonResult.COMPLETED_FLAG_JUMPED_BACK = 2;
LessonResult.COMPLETED_FLAG_NONE = 0;
LessonResult.COMPLETED_FLAG_SKIPPED = 4;
LessonResult.MODE_FLAG_CLASS_TOPIC_FOCUS = 1;
LessonResult.MODE_FLAG_NONE = 0;
LessonResult.creatableFields = [
    'completed',
    'completed_flag',
    'created',
    'help1',
    'help2',
    'help3',
    'legacy',
    'lesson',
    'mode',
    'mode_flag',
    'questions_answered',
    'questions_total',
    'score',
    'time_taken',
    'detailedTimestamp',
];
LessonResult.updatableFields = [
    'completed',
    'completed_flag',
    'help1',
    'help2',
    'help3',
    'legacy',
    'questions_answered',
    'questions_total',
    'score',
    'time_taken',
    'detailedTimestamp',
];

class School extends GuardRepositoryModel {
    constructor({ country, id, locale, name, timezone } = {}) {
        super();
        this.country = School.castToString(country);
        this.id = School.castToInteger(id);
        this.name = School.castToString(name);
        this.timezone = School.castToString(timezone);
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
    }
    get type() { return 'school'; }
    getCountry() {
        return this.country;
    }
    setCountry(country) {
        this.country = School.castToString(country);
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = School.castToInteger(id);
        return this;
    }
    getLocale() {
        return this.locale;
    }
    setLocale(locale) {
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
        return this;
    }
    getName() {
        return this.name;
    }
    setName(name) {
        this.name = School.castToString(name);
        return this;
    }
    getTimezone() {
        return this.timezone;
    }
    setTimezone(timezone) {
        this.timezone = School.castToString(timezone);
        return this;
    }
}

class Sticker extends GuardRepositoryModel {
    constructor({ id, locale } = {}) {
        super();
        this.id = Sticker.castToInteger(id);
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = Sticker.castToInteger(id);
        return this;
    }
    getLocale() {
        return this.locale;
    }
    setLocale(locale) {
        if (locale) {
            this.locale = locale instanceof Locale ? locale : new Locale(locale);
        }
        else {
            this.locale = new Locale();
        }
        return this;
    }
    get url() {
        if (!this.id) {
            return null;
        }
        return `https://${process.env.HOSTNAME_MY || process.env.REACT_APP_HOSTNAME_MY}/img/sticker/${this.locale.id}/${this.id}.png`;
    }
}

const getUserOrSchoolFromInputParam = (inputParam) => {
    return (inputParam instanceof User || inputParam instanceof School) ?
        inputParam :
        ((inputParam && inputParam.type) ?
            User.createFromType(inputParam.type, inputParam) :
            new School(inputParam !== null && inputParam !== void 0 ? inputParam : {}));
};
class Message extends GuardRepositoryModel {
    constructor({ body, from, from_display, id, read, reply_to_message_id, reply_to_message_from_display, sent, sticker, subject, to, to_multiple, } = {}) {
        super();
        this.body = Message.castToString(body);
        this.read = Message.castToBoolean(read);
        this.reply_to_message_from_display = Message.castToString(reply_to_message_from_display);
        this.subject = Message.castToString(subject);
        this.from_display = Message.castToString(from_display);
        this.sent = Message.castToDate(sent);
        this.id = Message.castToInteger(id);
        this.reply_to_message_id = Message.castToInteger(reply_to_message_id);
        this.from = getUserOrSchoolFromInputParam(from);
        this.to = getUserOrSchoolFromInputParam(to);
        this.sticker = sticker ? new Sticker(sticker) : null;
        if (to_multiple) {
            this.to_multiple = to_multiple
                .map((student) => {
                return student instanceof Student ? student : new Student(student);
            });
        }
        else {
            this.to_multiple = null;
        }
    }
    getBody() {
        return this.body;
    }
    setBody(body) {
        this.body = Message.castToString(body);
        return this;
    }
    getFrom() {
        return this.from;
    }
    setFrom(from) {
        this.from = getUserOrSchoolFromInputParam(from);
        return this;
    }
    getFromDisplay() {
        return this.from_display;
    }
    setFromDisplay(from_display) {
        this.from_display = Message.castToString(from_display);
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = User.castToInteger(id);
        return this;
    }
    getRead() {
        return this.read;
    }
    setRead(read) {
        this.read = Message.castToBoolean(read);
        return this;
    }
    getReplyToMessageFromDisplay() {
        return this.reply_to_message_from_display;
    }
    setReplyToMessageFromDisplay(reply_to_message_from_display) {
        this.reply_to_message_from_display = Message.castToString(reply_to_message_from_display);
        return this;
    }
    getReplyToMessageId() {
        return this.reply_to_message_id;
    }
    setReplyToMessageId(reply_to_message_id) {
        this.reply_to_message_id = Message.castToInteger(reply_to_message_id);
        return this;
    }
    getSent() {
        return this.sent;
    }
    setSent(sent) {
        this.sent = Message.castToDate(sent);
        return this;
    }
    getSticker() {
        return this.sticker;
    }
    setSticker(sticker) {
        if (sticker) {
            this.sticker = new Sticker(sticker);
        }
        return this;
    }
    getSubject() {
        return this.subject;
    }
    setSubject(subject) {
        this.subject = Message.castToString(subject);
        return this;
    }
    getTo() {
        return this.to;
    }
    setTo(to) {
        this.to = getUserOrSchoolFromInputParam(to);
        return this;
    }
    setToMultiple(to_multiple) {
        if (to_multiple) {
            this.to_multiple = to_multiple
                .map((student) => {
                return student instanceof Student ? student : new Student(student);
            });
        }
        else {
            this.to_multiple = null;
        }
        return this;
    }
    getToMultiple() {
        return this.to_multiple;
    }
}
Message.creatableFields = [
    'body',
    'from_display',
    'reply_to_message_id',
    'sticker',
    'subject',
    'to',
    'to_multiple',
];
Message.updatableFields = ['read'];

class OrganizationContact extends User {
}

class Parent extends User {
    constructor({ email, country, first_name, id, last_name, locale, partner_id, partner_managed, partner_name, partner_url, partner_user_id, sso_access_id, timezone, username, } = {}) {
        super({
            country,
            email,
            first_name,
            id,
            last_name,
            locale,
            partner_id,
            partner_managed,
            partner_name,
            partner_url,
            partner_user_id,
            sso_access_id,
            timezone,
            username,
        });
        this.preference = new Preference();
    }
    getPreference() {
        return this.preference;
    }
    setPreference(preference) {
        this.preference = preference;
        return this;
    }
}
Parent.creatableFields = [
    'country',
    'email',
    'locale',
].concat(User.creatableFields);
Parent.updatableFields = [
    'email',
    'preference',
].concat(User.updatableFields);

class Preference extends GuardRepositoryModel {
    constructor({ notifications } = {}) {
        super();
        this.notifications = notifications;
    }
}

const mathsAgeFormatter = format('.2f');
const usageAverageFormatter = Math.round;
const progressionsAverageFormatter = format('.1f');
class ReportModel extends GuardRepositoryModel {
    constructor({ end, start } = {}) {
        super();
        this.end = ReportModel.castToDate(end);
        this.start = ReportModel.castToDate(start);
    }
    getEnd() {
        return this.end;
    }
    setEnd(end) {
        this.end = ReportModel.castToDate(end);
        return this;
    }
    getStart() {
        return this.start;
    }
    setStart(start) {
        this.start = ReportModel.castToDate(start);
        return this;
    }
    get timespan_days() {
        if (!this.start || !this.end) {
            return null;
        }
        // Minimum 1 day (ceiled)
        return Math.max(1, Math.ceil(this.end.diff(this.start).as('days')));
    }
    get timespan_weeks() {
        if (!this.start || !this.end) {
            return null;
        }
        // Minimum 1 week (rounded to 2dp)
        return Math.max(1, Math.round(this.end.diff(this.start).as('weeks') * 100) / 100);
    }
    get timespan_years() {
        if (!this.start || !this.end) {
            return null;
        }
        // Minimum 0.02 years (1 week) (rounded to 2dp)
        return Math.max(0.02, Math.round(this.end.diff(this.start).as('years') * 100) / 100);
    }
    static castToMathsAge(value) {
        const float = ReportModel.castToFloat(value);
        return null != float ? mathsAgeFormatter(float) : null;
    }
    static castToMathsAgeAverage(value) {
        return this.castToMathsAge(value);
    }
    static castToProgressions(value) {
        return ReportModel.castToInteger(value);
    }
    static castToProgressionsAverage(value) {
        const float = ReportModel.castToFloat(value);
        return null != float ? progressionsAverageFormatter(float) : null;
    }
    static castToUsage(value) {
        return ReportModel.castToInteger(value);
    }
    static castToUsageAverage(value) {
        const float = ReportModel.castToFloat(value);
        return null != float ? usageAverageFormatter(float) : null;
    }
    static formatImprovement(value) {
        return `${Number.parseFloat(value) > 0 ? '+' : ''}${value}`;
    }
}

class SchoolLeaderboard extends Leaderboard {
    constructor({ school_id, entries } = {}) {
        super({ entries });
        this.school_id = SchoolLeaderboard.castToInteger(school_id);
    }
    getSchoolId() {
        return this.school_id;
    }
    setSchoolId(school_id) {
        this.school_id = SchoolLeaderboard.castToInteger(school_id);
        return this;
    }
}

class SubscriptionPlan extends GuardRepositoryModel {
    constructor({ amount, currency, discount, discount_duration, id, interval, sibling_discount_amount, trial_period, } = {}) {
        super();
        this.amount = SubscriptionPlan.castToFloat(amount);
        this.currency = SubscriptionPlan.castToString(currency);
        this.discount = SubscriptionPlan.castToFloat(discount);
        this.discount_duration = SubscriptionPlan.castToString(discount_duration);
        this.id = SubscriptionPlan.castToString(id);
        this.interval = SubscriptionPlan.castToString(interval);
        this.sibling_discount_amount = SubscriptionPlan.castToFloat(sibling_discount_amount);
        this.trial_period = SubscriptionPlan.castToInteger(trial_period);
    }
    static formatAmount(locale, currency, amount, removeZeroFractionDigits = true) {
        let localeParsed;
        if (!locale) {
            localeParsed = new Locale();
        }
        else if (!(locale instanceof Locale)) {
            localeParsed = new Locale({ id: locale });
        }
        else {
            localeParsed = locale;
        }
        const options = {
            currency,
            style: 'currency',
        };
        // Round the amount to 2dp to remove float precision issues
        const amountParsed = Math.round((amount + Number.EPSILON) * 100) / 100;
        if (removeZeroFractionDigits && amountParsed === parseInt(amountParsed.toString())) {
            options.minimumFractionDigits = 0;
            options.maximumFractionDigits = 0;
        }
        return new Intl.NumberFormat(`${localeParsed.language}-${localeParsed.country.toUpperCase()}`, options).format(amount);
    }
    get effective_amount() {
        return Math.max(0, this.discount ? Math.round((this.amount - this.discount) * 100) / 100 : this.amount);
    }
    formatAmount(locale, amount = null, removeZeroFractionDigits = true) {
        return SubscriptionPlan.formatAmount(locale, this.currency, null === amount ? this.effective_amount : amount, removeZeroFractionDigits);
    }
    getAmount() {
        return this.amount;
    }
    setAmount(amount) {
        this.amount = SubscriptionPlan.castToFloat(amount);
        return this;
    }
    getCurrency() {
        return this.currency;
    }
    setCurrency(currency) {
        this.currency = SubscriptionPlan.castToString(currency);
        return this;
    }
    getDiscount() {
        return this.discount;
    }
    setDiscount(discount) {
        this.discount = SubscriptionPlan.castToFloat(discount);
        return this;
    }
    getDiscountDuration() {
        return this.discount_duration;
    }
    setDiscountDuration(discount_duration) {
        this.discount_duration = SubscriptionPlan.castToString(discount_duration);
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = SubscriptionPlan.castToString(id);
        return this;
    }
    getInterval() {
        return this.interval;
    }
    setInterval(interval) {
        this.interval = SubscriptionPlan.castToString(interval);
        return this;
    }
    getSiblingDiscountAmount() {
        return this.sibling_discount_amount;
    }
    setSiblingDiscountAmount(sibling_discount_amount) {
        this.sibling_discount_amount = SubscriptionPlan.castToFloat(sibling_discount_amount);
        return this;
    }
    getTrialPeriod() {
        return this.trial_period;
    }
    setTrialPeriod(trial_period) {
        this.trial_period = SubscriptionPlan.castToInteger(trial_period);
        return this;
    }
}

class Subscription extends GuardRepositoryModel {
    constructor({ canceled, end, grace, grace_requires_action, id, next_plan, plan, start, student_id, trial, locale, subscription_product_country, subscription_product_type, }) {
        super();
        this.canceled = Subscription.castToBoolean(canceled);
        this.end = Subscription.castToDate(end);
        this.grace = Subscription.castToBoolean(grace);
        this.grace_requires_action = Subscription.castToBoolean(grace_requires_action);
        this.id = Subscription.castToString(id);
        this.locale = locale instanceof Locale ? locale : new Locale(locale);
        this.subscription_product_country = subscription_product_country;
        this.subscription_product_type = subscription_product_type;
        if (next_plan) {
            this.next_plan = next_plan instanceof SubscriptionPlan ? next_plan : new SubscriptionPlan(next_plan);
        }
        else {
            this.next_plan = null;
        }
        if (plan) {
            this.plan = plan instanceof SubscriptionPlan ? plan : new SubscriptionPlan(plan);
        }
        else {
            this.plan = null;
        }
        this.start = Subscription.castToDate(start);
        this.student_id = Subscription.castToInteger(student_id);
        this.trial = Subscription.castToBoolean(trial);
    }
    getCanceled() {
        return this.canceled;
    }
    setCanceled(canceled) {
        this.canceled = Subscription.castToBoolean(canceled);
        return this;
    }
    getEnd() {
        return this.end;
    }
    setEnd(end) {
        this.end = Subscription.castToDate(end);
        return this;
    }
    getGrace() {
        return this.grace;
    }
    setGrace(grace) {
        this.grace = Subscription.castToBoolean(grace);
        return this;
    }
    getGraceRequiresAction() {
        return this.grace_requires_action;
    }
    setGraceRequiresAction(grace_requires_action) {
        this.grace_requires_action = Subscription.castToBoolean(grace_requires_action);
        return this;
    }
    getId() {
        return this.id;
    }
    setId(id) {
        this.id = Subscription.castToString(id);
        return this;
    }
    getLocale() {
        return this.locale;
    }
    setLocale(locale) {
        this.locale = locale instanceof Locale ? locale : new Locale(locale);
        return this;
    }
    getNextPlan() {
        return this.next_plan;
    }
    setNextPlan(next_plan) {
        if (next_plan) {
            this.next_plan = next_plan instanceof SubscriptionPlan ? next_plan : new SubscriptionPlan(next_plan);
        }
        else {
            this.next_plan = null;
        }
        return this;
    }
    getPlan() {
        return this.plan;
    }
    setPlan(plan) {
        if (plan) {
            this.plan = plan instanceof SubscriptionPlan ? plan : new SubscriptionPlan(plan);
        }
        else {
            this.plan = null;
        }
        return this;
    }
    getStart() {
        return this.start;
    }
    setStart(start) {
        this.start = Subscription.castToDate(start);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(student_id) {
        this.student_id = Subscription.castToInteger(student_id);
        return this;
    }
    getTrial() {
        return this.trial;
    }
    setTrial(trial) {
        this.trial = Subscription.castToBoolean(trial);
        return this;
    }
    getSubscriptionProductCountry() {
        return this.subscription_product_country;
    }
    setSubscriptionProductCountry(country) {
        this.subscription_product_country = country;
        return this;
    }
    getSubscriptionProductType() {
        return this.subscription_product_type;
    }
    setSubscriptionProductType(type) {
        this.subscription_product_type = type;
        return this;
    }
}
Subscription.creatableFields = [
    'locale',
    'plan',
];
Subscription.updatableFields = [
    'canceled',
    'next_plan',
];

class SiblingDiscount extends GuardRepositoryModel {
    constructor({ currency, amount, lead_subscription } = {}) {
        super();
        this.currency = SiblingDiscount.castToString(currency);
        this.amount = SiblingDiscount.castToFloat(amount);
        if (lead_subscription) {
            this.lead_subscription = lead_subscription instanceof Subscription
                ? lead_subscription
                : new Subscription(lead_subscription);
        }
        else {
            this.lead_subscription = null;
        }
    }
    getAmount() {
        return this.amount;
    }
    setAmount(amount) {
        this.amount = SiblingDiscount.castToFloat(amount);
        return this;
    }
    getCurrency() {
        return this.currency;
    }
    setCurrency(currency) {
        this.currency = SiblingDiscount.castToString(currency);
        return this;
    }
    getLeadSubscription() {
        return this.lead_subscription;
    }
    setLeadSubscription(lead_subscription) {
        if (lead_subscription) {
            this.lead_subscription = lead_subscription instanceof Subscription
                ? lead_subscription
                : new Subscription(lead_subscription);
        }
        else {
            this.lead_subscription = null;
        }
        return this;
    }
    formatAmount(locale /*removeZeroFractionDigits = true*/) {
        return SubscriptionPlan.formatAmount(locale, this.currency, this.amount /*, removeZeroFractionDigits*/);
    }
}

class StudentReportModel extends ReportModel {
    constructor({ student_id, end, start } = {}) {
        super({ end, start });
        this.student_id = StudentReportModel.castToInteger(student_id);
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(studentId) {
        this.student_id = StudentReportModel.castToInteger(studentId);
        return this;
    }
}

class StudentMathsAgeReport extends StudentReportModel {
    constructor({ student_id, end, start, actual_age, end_age, start_age, } = {}) {
        var _a, _b, _c;
        super({ student_id, end, start });
        this.actual_age = (_a = StudentMathsAgeReport.castToFloat(StudentMathsAgeReport.castToMathsAge(actual_age))) !== null && _a !== void 0 ? _a : 0;
        this.end_age = (_b = StudentMathsAgeReport.castToFloat(StudentMathsAgeReport.castToMathsAge(end_age))) !== null && _b !== void 0 ? _b : 0;
        this.start_age = (_c = StudentMathsAgeReport.castToFloat(StudentMathsAgeReport.castToMathsAge(start_age))) !== null && _c !== void 0 ? _c : 0;
    }
    getActualAge() {
        return this.actual_age;
    }
    setActualAge(actual_age) {
        var _a;
        this.actual_age = (_a = StudentMathsAgeReport.castToFloat(StudentMathsAgeReport.castToMathsAge(actual_age))) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
    getEndAge() {
        return this.end_age;
    }
    setEndAge(end_age) {
        var _a;
        this.end_age = (_a = StudentMathsAgeReport.castToFloat(StudentMathsAgeReport.castToMathsAge(end_age))) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
    getStartAge() {
        return this.start_age;
    }
    setStartAge(start_age) {
        var _a;
        this.start_age = (_a = StudentMathsAgeReport.castToFloat(StudentMathsAgeReport.castToMathsAge(start_age))) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
    get improvement() {
        return StudentMathsAgeReport.formatImprovement(StudentMathsAgeReport.castToMathsAge(StudentMathsAgeReport.castToFloat(this.end_age) - StudentMathsAgeReport.castToFloat(this.start_age)));
    }
}

class StudentProgressionReport extends StudentReportModel {
    constructor({ student_id, end, start, progressions } = {}) {
        var _a;
        super({ student_id, end, start });
        this.progressions = (_a = StudentProgressionReport.castToProgressions(progressions)) !== null && _a !== void 0 ? _a : 0;
    }
    get average() {
        return StudentProgressionReport.castToProgressionsAverage((this.timespan_weeks && this.progressions) ? this.progressions / this.timespan_weeks : 0);
    }
    getProgressions() {
        return this.progressions;
    }
    setProgressions(progressions) {
        var _a;
        this.progressions = (_a = StudentProgressionReport.castToProgressions(progressions)) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
}

class StudentTopicsReport extends StudentReportModel {
    constructor({ student_id, end, start, topics, end_ages, start_ages, } = {}) {
        super({ student_id, end, start });
        this.end_ages = {};
        this.start_ages = {};
        this.topics = topics ? topics.map((topic) => new Topic(topic)) : [];
        this.end_ages = end_ages ? StudentTopicsReport.castAges(end_ages) : {};
        this.start_ages = start_ages ? StudentTopicsReport.castAges(start_ages) : {};
    }
    getEndAges() {
        return this.end_ages;
    }
    setEndAges(end_ages) {
        this.end_ages = end_ages ? StudentTopicsReport.castAges(end_ages) : {};
        return this;
    }
    getStartAges() {
        return this.start_ages;
    }
    setStartAges(start_ages) {
        this.start_ages = start_ages ? StudentTopicsReport.castAges(start_ages) : {};
        return this;
    }
    getTopics() {
        return this.topics;
    }
    setTopics(topics) {
        this.topics = topics ? topics.map((topic) => new Topic(topic)) : [];
        return this;
    }
    static castAges(value) {
        const casted = {};
        Object.keys(value).forEach((key) => {
            casted[key] = StudentTopicsReport.castToMathsAge(value[key]);
        });
        return casted;
    }
    get start_age_average() {
        const entries = Object.entries(this.start_ages);
        return StudentTopicsReport.castToMathsAgeAverage(entries.length
            ? entries.reduce((totalAge, [, topicAge]) => totalAge + StudentTopicsReport.castToFloat(topicAge), 0) / entries.length
            : 0);
    }
    get end_age_average() {
        const entries = Object.entries(this.end_ages);
        return StudentTopicsReport.castToMathsAgeAverage(entries.length
            ? entries.reduce((totalAge, [, topicAge]) => totalAge + StudentTopicsReport.castToFloat(topicAge), 0) / entries.length
            : 0);
    }
    get improvement() {
        return StudentTopicsReport.formatImprovement(StudentTopicsReport.castToMathsAge(StudentTopicsReport.castToFloat(this.end_age_average) - StudentTopicsReport.castToFloat(this.start_age_average)));
    }
    topicEndAge(topic) {
        let topicParsed;
        if (topic instanceof Topic) {
            topicParsed = topic;
        }
        else {
            topicParsed = new Topic({ id: topic.id, name: topic.name });
        }
        return this.end_ages[topicParsed.id];
    }
    topicImprovement(topic) {
        return StudentTopicsReport.formatImprovement(StudentTopicsReport.castToMathsAge(StudentTopicsReport.castToFloat(this.topicEndAge(topic)) - StudentTopicsReport.castToFloat(this.topicStartAge(topic))));
    }
    topicStartAge(topic) {
        let topicParsed;
        if (topic instanceof Topic) {
            topicParsed = topic;
        }
        else {
            topicParsed = new Topic({ id: topic.id, name: topic.name });
        }
        return this.start_ages[topicParsed.id];
    }
}

class Usage extends GuardRepositoryModel {
    constructor({ assessment, replay, tutor } = {}) {
        var _a, _b, _c;
        super();
        this.assessment = (_a = Usage.castToInteger(assessment)) !== null && _a !== void 0 ? _a : 0;
        this.replay = (_b = Usage.castToInteger(replay)) !== null && _b !== void 0 ? _b : 0;
        this.tutor = (_c = Usage.castToInteger(tutor)) !== null && _c !== void 0 ? _c : 0;
    }
    get total() {
        if (null === this.replay && null === this.tutor) {
            return null;
        }
        return this.replay + this.tutor;
    }
    getAssessment() {
        return this.assessment;
    }
    setAssessment(assessment) {
        var _a;
        this.assessment = (_a = Usage.castToInteger(assessment)) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
    getReplay() {
        return this.replay;
    }
    setReplay(replay) {
        var _a;
        this.replay = (_a = Usage.castToInteger(replay)) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
    getTutor() {
        return this.tutor;
    }
    setTutor(tutor) {
        var _a;
        this.tutor = (_a = Usage.castToInteger(tutor)) !== null && _a !== void 0 ? _a : 0;
        return this;
    }
}

class StudentUsageReport extends StudentReportModel {
    constructor({ student_id, end, start, usage } = {}) {
        super({ student_id, end, start });
        if (usage) {
            this.usage = usage instanceof Usage ? usage : new Usage(usage);
        }
        else {
            this.usage = new Usage();
        }
    }
    average(type = 'total') {
        return StudentUsageReport.castToUsageAverage((this.timespan_weeks && this.usage[type]) ? this.usage[type] / this.timespan_weeks : 0);
    }
    getUsage() {
        return this.usage;
    }
    setUsage(usage) {
        if (usage) {
            this.usage = usage instanceof Usage ? usage : new Usage(usage);
        }
        else {
            this.usage = new Usage();
        }
        return this;
    }
}

class Teacher extends User {
    constructor({ country, email, first_name, id, last_name, locale, partner_id, partner_managed, partner_name, partner_url, partner_user_id, school_id, sso_access_id, timezone, username, isAdmin, cleverId, } = {}) {
        super({
            country,
            email,
            first_name,
            id,
            last_name,
            locale,
            partner_id,
            partner_managed,
            partner_name,
            partner_url,
            partner_user_id,
            sso_access_id,
            timezone,
            username,
        });
        this.isAdmin = isAdmin !== null && isAdmin !== void 0 ? isAdmin : false;
        this.school_id = school_id !== null && school_id !== void 0 ? school_id : undefined;
        this.cleverId = cleverId !== null && cleverId !== void 0 ? cleverId : null;
    }
    setSchoolId(schoolId) {
        this.school_id = schoolId;
        return this;
    }
    getSchoolId() {
        return this.school_id;
    }
    setIsAdmin(isAdmin) {
        this.isAdmin = isAdmin;
        return this;
    }
    getIsAdmin() {
        return this.isAdmin;
    }
    getCleverId() {
        return this.cleverId;
    }
    setCleverId(cleverId) {
        this.cleverId = cleverId;
        return this;
    }
}

class TopicProgress extends GuardRepositoryModel {
    constructor({ age, completed_year, remaining_year, student_id, topic, years, } = {}) {
        super();
        this.age = TopicProgress.castToFloat(age);
        this.completed_year = TopicProgress.castToInteger(completed_year);
        this.remaining_year = TopicProgress.castToInteger(remaining_year);
        this.student_id = TopicProgress.castToInteger(student_id);
        if (topic) {
            this.topic = topic instanceof Topic ? topic : new Topic(topic);
        }
        else {
            this.topic = new Topic();
        }
        if (years) {
            this.years = years.map((year) => TopicProgress.castToInteger(year));
        }
        else {
            this.years = [];
        }
    }
    getAge() {
        return this.age;
    }
    setAge(age) {
        this.age = TopicProgress.castToFloat(age);
        return this;
    }
    getCompletedYear() {
        return this.completed_year;
    }
    setCompletedYear(completed_year) {
        this.completed_year = TopicProgress.castToInteger(completed_year);
        return this;
    }
    getRemainingYear() {
        return this.remaining_year;
    }
    setRemainingYear(remaining_year) {
        this.remaining_year = TopicProgress.castToInteger(remaining_year);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(studentId) {
        this.student_id = TopicProgress.castToInteger(studentId);
        return this;
    }
    getTopic() {
        return this.topic;
    }
    setTopic(topic) {
        if (topic) {
            this.topic = topic instanceof Topic ? topic : new Topic(topic);
        }
        else {
            this.topic = new Topic();
        }
        return this;
    }
    getYears() {
        return this.years;
    }
    setYears(years) {
        if (years) {
            this.years = years.map((year) => TopicProgress.castToInteger(year));
        }
        else {
            this.years = [];
        }
        return this;
    }
}

class TopicReplay extends GuardRepositoryModel {
    constructor({ student_id, topic, lessons_attempted, lessons_unattempted, } = {}) {
        super();
        this.student_id = TopicReplay.castToInteger(student_id);
        if (topic) {
            this.topic = topic instanceof Topic ? topic : new Topic(topic);
        }
        else {
            this.topic = new Topic();
        }
        if (lessons_attempted) {
            this.lessons_attempted = lessons_attempted
                .map((lesson_attempted) => {
                return lesson_attempted instanceof LessonResult ? lesson_attempted : new LessonResult(lesson_attempted);
            });
        }
        else {
            this.lessons_attempted = [];
        }
        if (lessons_unattempted) {
            this.lessons_unattempted = lessons_unattempted
                .map((lesson_unattempted) => {
                return lesson_unattempted instanceof Lesson ? lesson_unattempted : new Lesson(lesson_unattempted);
            });
        }
        else {
            this.lessons_unattempted = [];
        }
    }
    getLessonsAttempted() {
        return this.lessons_attempted;
    }
    setLessonsAttempted(lessons_attempted) {
        if (lessons_attempted) {
            this.lessons_attempted = lessons_attempted
                .map((lesson_attempted) => {
                return lesson_attempted instanceof LessonResult ? lesson_attempted : new LessonResult(lesson_attempted);
            });
        }
        else {
            this.lessons_attempted = [];
        }
        return this;
    }
    getLessonsUnattempted() {
        return this.lessons_unattempted;
    }
    setLessonsUnattempted(lessons_unattempted) {
        if (lessons_unattempted) {
            this.lessons_unattempted = lessons_unattempted
                .map((lesson_unattempted) => {
                return lesson_unattempted instanceof Lesson ? lesson_unattempted : new Lesson(lesson_unattempted);
            });
        }
        else {
            this.lessons_unattempted = [];
        }
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(studentId) {
        this.student_id = TopicReplay.castToInteger(studentId);
        return this;
    }
    getTopic() {
        return this.topic;
    }
    setTopic(topic) {
        if (topic) {
            this.topic = topic instanceof Topic ? topic : new Topic(topic);
        }
        else {
            this.topic = new Topic();
        }
        return this;
    }
}

class WeeklySummary extends GuardRepositoryModel {
    constructor({ progressions, replays, student_id, week_starting, usage } = {}) {
        super();
        this.week_starting = WeeklySummary.castToDate(week_starting);
        this.progressions = WeeklySummary.castToInteger(progressions);
        this.replays = WeeklySummary.castToInteger(replays);
        this.student_id = WeeklySummary.castToInteger(student_id);
        if (usage) {
            this.usage = usage instanceof Usage ? usage : new Usage(usage);
        }
        else {
            this.usage = new Usage();
        }
    }
    getProgressions() {
        return this.progressions;
    }
    setProgressions(progressions) {
        this.progressions = WeeklySummary.castToInteger(progressions);
        return this;
    }
    getReplays() {
        return this.replays;
    }
    setReplays(replays) {
        this.replays = WeeklySummary.castToInteger(replays);
        return this;
    }
    getStudentId() {
        return this.student_id;
    }
    setStudentId(studentId) {
        this.student_id = WeeklySummary.castToInteger(studentId);
        return this;
    }
    getUsage() {
        return this.usage;
    }
    setUsage(usage) {
        if (usage) {
            this.usage = usage instanceof Usage ? usage : new Usage(usage);
        }
        else {
            this.usage = new Usage();
        }
        return this;
    }
    getWeekStarting() {
        return this.week_starting;
    }
    setWeekStarting(weekStarting) {
        this.week_starting = WeeklySummary.castToDate(weekStarting);
        return this;
    }
}

class NewStudentBasic extends GuardRepositoryModel {
    constructor({ first_name, id, last_name, username }) {
        super();
        this.first_name = first_name;
        this.id = id;
        this.last_name = last_name;
        this.username = username;
    }
    getId() {
        return this.id;
    }
    getUsername() {
        return this.username;
    }
    getName(type = 'FULL') {
        switch (type) {
            case 'FULL':
                return `${NewStudentBasic.ucWord(this.first_name)} ${NewStudentBasic.ucWord(this.last_name)}`;
            case 'FIRST':
                return NewStudentBasic.ucWord(this.first_name);
            case 'LAST':
                return NewStudentBasic.ucWord(this.last_name);
            default:
                return '';
        }
    }
    static ucWord(word) {
        return `${word.substr(0, 1).toUpperCase()}${word.substr(1)}`;
    }
}

class NewStudentWithTags extends NewStudentBasic {
    constructor({ first_name, id, last_name, username, tags }) {
        super({ first_name, id, last_name, username });
        this.tags = tags;
    }
    getTags() {
        return this.tags;
    }
    setTags(tags) {
        this.tags = tags;
        return this;
    }
    hasTag(id) {
        return this.tags.includes(id);
    }
}

class NewLessonOutcome extends GuardRepositoryModel {
    constructor({ completed, lessonID, test, timeStamp, time_Taken, title, outcome, expectedTimeTaken, topic, isJumpingMode, stackDepth, }) {
        super();
        this.completed = completed;
        this.lessonID = lessonID;
        this.test = test;
        this.time_Taken = time_Taken;
        this.title = title;
        this.outcome = outcome;
        this.timeStamp = DateTime.isDateTime(timeStamp) ? timeStamp : DateTime.fromISO(timeStamp);
        this.expectedTimeTaken = expectedTimeTaken;
        this.topic = topic;
        this.isJumpingMode = isJumpingMode;
        this.stackDepth = stackDepth;
    }
    getLessonID() {
        return this.lessonID;
    }
    getCompleted() {
        return this.completed;
    }
    getTimeStamp() {
        return this.timeStamp;
    }
    getTimeTaken() {
        return this.time_Taken;
    }
    getTest() {
        return this.test;
    }
    getTitle() {
        return this.title;
    }
    getOutcome() {
        return this.outcome;
    }
    getExpectedTimeTaken() {
        return this.expectedTimeTaken;
    }
    getTopic() {
        return this.topic;
    }
    getIsJumpingMode() {
        return this.isJumpingMode;
    }
    getStackDepth() {
        return this.stackDepth;
    }
}

class NewStudentTutorData extends NewStudentWithTags {
    constructor({ first_name, id, last_name, username, lastLessonOutcomes, last_logon_date, maths_age, progressions, tags, isBeingReassessed, hasBeenReassessed, reassessmentEndDate, haveCompletedAssessment, haveNotCompletedRelevantDate, weekLearningTime, weekProgression, weeksLearningTime, weeksProgression, weeksMathsAgeDelta, percentageCompletedLastAssessment, is_assessment_reset, gender, }) {
        super({ first_name, id, last_name, username, tags });
        this.lastLessonOutcomes = lastLessonOutcomes.map((lastLessonOutcome) => new NewLessonOutcome(lastLessonOutcome));
        this.last_logon_date = last_logon_date ? (DateTime.isDateTime(last_logon_date) ? last_logon_date : DateTime.fromISO(last_logon_date)) : null;
        this.reassessmentEndDate = reassessmentEndDate ? (DateTime.isDateTime(reassessmentEndDate) ? reassessmentEndDate : DateTime.fromISO(reassessmentEndDate)) : null;
        this.haveNotCompletedRelevantDate = DateTime.isDateTime(haveNotCompletedRelevantDate) ? haveNotCompletedRelevantDate : DateTime.fromISO(haveNotCompletedRelevantDate);
        this.maths_age = maths_age;
        this.progressions = progressions;
        this.isBeingReassessed = isBeingReassessed;
        this.hasBeenReassessed = hasBeenReassessed;
        this.haveCompletedAssessment = haveCompletedAssessment;
        this.weekLearningTime = weekLearningTime;
        this.weekProgression = weekProgression;
        this.weeksLearningTime = weeksLearningTime;
        this.weeksProgression = weeksProgression;
        this.weeksMathsAgeDelta = weeksMathsAgeDelta;
        this.percentageCompletedLastAssessment = percentageCompletedLastAssessment;
        this.is_assessment_reset = is_assessment_reset;
        this.gender = gender;
    }
    isGoingWell() {
        // Fully started -> progressions > 0 and `last_logon_date` != null and
        // all `assessments`.`completed` records are null
        return this.getProgressions() > 0 && this.getLastLogonDate() !== null &&
            (this.getHaveCompletedAssessment() && this.isAssessmentReset());
    }
    isStuck() {
        // Filter by lesson completed with certain outcome. If failed in the last 8 weeks or from reassessment date, then look at the full history
        try {
            this.getStuckLesson();
            return true;
        }
        catch (e) {
            return false;
        }
    }
    getStuckLesson() {
        const completedLessons = this.getOrderedLastLessonOutcomesByLessonIDAndTimeStamp().filter((lesson) => lesson.getCompleted()
            && lesson.getOutcome()
            && ['failed', 'static'].includes(lesson.getOutcome())
            && (this.getHasBeenReassessed() ?
                lesson.getTimeStamp() > this.getReassessmentEndDate() :
                lesson.getTimeStamp() > DateTime.now().minus({ weeks: 8 }) // TODO: Nic thinks we can improve this line going to its first assessment date
            ));
        const error = new Error('No stuck lesson were found in the given object');
        if (completedLessons.length === 0) {
            throw error;
        }
        let count = 1;
        let lastSortedLesson = completedLessons.shift();
        for (const sortedLesson of completedLessons) {
            if (lastSortedLesson.getLessonID() !== sortedLesson.getLessonID()) {
                count = 1;
                lastSortedLesson = sortedLesson;
                continue;
            }
            if (++count > 1 && this.hasNeverPassedLesson(sortedLesson)) {
                return sortedLesson;
            }
        }
        throw new Error('No stuck lesson were found in the given object');
    }
    isStruggling() {
        const a = this.getLastLessonOutcomes()
            .filter((lesson) => (this.getHasBeenReassessed() ? lesson.getTimeStamp() > this.getReassessmentEndDate() : true))
            .slice(0, 10)
            .filter((lesson) => lesson.getCompleted() && lesson.getOutcome() !== 'passed');
        return a.length >= 4;
    }
    getOrderedLastLessonOutcomesByLessonIDAndTimeStamp() {
        return this.getLastLessonOutcomes().sort((a, b) => {
            if (a.getLessonID() === b.getLessonID()) {
                if (a.getTimeStamp() === b.getTimeStamp()) {
                    return 0;
                }
                // Returns `a` before `b` if `a` is nearer in time
                return a.getTimeStamp() > b.getTimeStamp() ? -1 : 1;
            }
            return a.getLessonID() > b.getLessonID() ? -1 : 1;
        });
    }
    getProgressingSlowlyLessons() {
        // Supposing `lastLessonOutcomes` came ordered by `timestamp` in DESC order
        const lastLessonOutcomesPassed = this.getLastLessonOutcomes().filter((lesson) => !lesson.getIsJumpingMode() && lesson.getStackDepth() === 1);
        if (lastLessonOutcomesPassed.length < 5) {
            return [];
        }
        const lastFiveLessonOutcomes = lastLessonOutcomesPassed.slice(0, 5);
        const totalTimeTakenSeconds = lastFiveLessonOutcomes
            .map((lessonOutcome) => lessonOutcome.getTimeTaken())
            .reduce((accumulator, current) => accumulator + current, 0); // Sum time taken
        const totalExpectedTimeTakenSeconds = lastFiveLessonOutcomes
            .map((lessonOutcome) => lessonOutcome.getExpectedTimeTaken())
            .reduce((accumulator, current) => accumulator + current, 0); // Sum expected time taken
        if (totalTimeTakenSeconds > 1.5 * totalExpectedTimeTakenSeconds) {
            return lastFiveLessonOutcomes;
        }
        return [];
    }
    getFailedLessons() {
        return this.getLastLessonOutcomes().filter((lessonOutcome) => {
            return !lessonOutcome.getCompleted() && lessonOutcome.getTest();
        });
    }
    getPassedLessons() {
        return this.getLastLessonOutcomes().filter((lastLessonOutcome) => lastLessonOutcome.getCompleted());
    }
    getAverageProgressions() {
        return this.progressions;
    }
    getMathsAge() {
        return this.maths_age;
    }
    getLastLogonDate() {
        return this.last_logon_date;
    }
    getLastLessonOutcomes() {
        return this.lastLessonOutcomes;
    }
    setLastLessonOutcomes(lastLessonOutcomes) {
        this.lastLessonOutcomes = lastLessonOutcomes;
        return this;
    }
    getProgressions() {
        return this.progressions;
    }
    setProgressions(progressions) {
        this.progressions = progressions;
        return this;
    }
    getHasBeenReassessed() {
        return this.hasBeenReassessed;
    }
    setHasBeenReassessed(hasBeenReassessed) {
        this.hasBeenReassessed = hasBeenReassessed;
        return this;
    }
    getHaveCompletedAssessment() {
        return this.haveCompletedAssessment;
    }
    setHaveCompletedAssessment(haveCompletedAssessment) {
        this.haveCompletedAssessment = haveCompletedAssessment;
        return this;
    }
    getHaveNotCompletedRelevantDate() {
        return this.haveNotCompletedRelevantDate;
    }
    setHaveNotCompletedRelevantDate(haveNotCompletedRelevantDate) {
        this.haveNotCompletedRelevantDate = haveNotCompletedRelevantDate;
        return this;
    }
    getWeekLearningTime() {
        return this.weekLearningTime;
    }
    setWeekLearningTime(weekLearningTime) {
        this.weekLearningTime = weekLearningTime;
        return this;
    }
    getWeekProgression() {
        return this.weekProgression;
    }
    setWeekProgression(weekProgression) {
        this.weekProgression = weekProgression;
        return this;
    }
    getWeeksLearningTime() {
        return this.weeksLearningTime;
    }
    setWeeksLearningTime(weeksLearningTime) {
        this.weeksLearningTime = weeksLearningTime;
        return this;
    }
    getWeeksProgression() {
        return this.weeksProgression;
    }
    setWeeksProgression(weeksProgression) {
        this.weeksProgression = weeksProgression;
        return this;
    }
    getWeeksMathsAgeDelta() {
        return this.weeksMathsAgeDelta;
    }
    setWeeksMathsAgeDelta(weeksMathsAgeDelta) {
        this.weeksMathsAgeDelta = weeksMathsAgeDelta;
        return this;
    }
    setReassessmentEndDate(reassessmentEndDate) {
        this.reassessmentEndDate = reassessmentEndDate;
        return this;
    }
    getReassessmentEndDate() {
        return this.reassessmentEndDate;
    }
    hasNeverPassedLesson(sortedLesson) {
        return this.getLastLessonOutcomes().filter((lesson) => lesson.getLessonID() === sortedLesson.getLessonID() &&
            lesson.getCompleted() && lesson.getOutcome() === 'passed').length === 0;
    }
    setIsBeingReassessed(isBeingReassessed) {
        this.isBeingReassessed = isBeingReassessed;
        return this;
    }
    getIsBeingReassessed() {
        return this.isBeingReassessed;
    }
    setPercentageCompletedLastAssessment(percentageCompletedLastAssessment) {
        this.percentageCompletedLastAssessment = percentageCompletedLastAssessment;
        return this;
    }
    getPercentageCompletedLastAssessment() {
        return this.percentageCompletedLastAssessment;
    }
    setAssessmentReset(is_assessment_reset) {
        this.is_assessment_reset = is_assessment_reset;
        return this;
    }
    isAssessmentReset() {
        return this.is_assessment_reset;
    }
    getGender() {
        return this.gender;
    }
}

class GroupReport extends GuardRepositoryModel {
    constructor({ startDate, endDate, recentDate, students }) {
        super();
        this.startDate = DateTime.isDateTime(startDate) ? startDate : DateTime.fromISO(startDate);
        this.students = students.map((student) => new NewStudentTutorData(student));
        if (null != endDate) {
            this.endDate = DateTime.isDateTime(endDate) ? endDate : DateTime.fromISO(endDate);
        }
        else {
            this.endDate = DateTime.now();
        }
        if (null != recentDate) {
            this.recentDate = DateTime.isDateTime(recentDate) ? recentDate : DateTime.fromISO(recentDate);
        }
        else {
            this.recentDate = DateTime.now().startOf('week');
        }
    }
    getWithNoLogin() {
        // Never logged in: `last_logon_date` being null
        return this.students.filter((student) => null === student.getLastLogonDate());
    }
    getWithNoAssessment() {
        // haven't completed means `last_logon_date` != null and `Pupil`.`assessmentCompleted` == null && and
        // all `assessments`.`completed` records are null
        return this.students.filter((student) => {
            return null !== student.getLastLogonDate()
                && !student.getHaveCompletedAssessment()
                && !student.isAssessmentReset();
        });
    }
    getUnassessedOrReassessedStudents() {
        return this.students.filter((student) => {
            return null !== student.getLastLogonDate()
                && !student.getHaveCompletedAssessment();
        });
    }
    getAssessedNotBeingReassessedStudents() {
        // "isAssessmentReset" is not necessary as "getHaveCompletedAssessment" will be "false" if the reassessment
        // hasn't been completed
        return this.students.filter((student) => {
            return null !== student.getLastLogonDate()
                && student.getHaveCompletedAssessment();
        });
    }
    getWithNoProgression() {
        // haven't made progressions: progressions == 0 and `last_logon_date` != null and
        // `Pupil`.`assessmentCompleted` == null && and all `assessments`.`completed` records are not null
        return this.students.filter((student) => student.getProgressions() === 0 &&
            null !== student.getLastLogonDate() &&
            (student.getHaveCompletedAssessment() || student.isAssessmentReset()));
    }
    getGoingWell() {
        return this.students.filter((student) => student.getProgressions() > 0 &&
            null !== student.getLastLogonDate() &&
            (student.getHaveCompletedAssessment() || student.isAssessmentReset()));
    }
    getStuck() {
        return this.students.filter((student) => student.isStuck());
    }
    getProgressingSlowly() {
        return this.students.filter((student) => student.getProgressingSlowlyLessons().length !== 0);
    }
    getFailingSameLesson() {
        // TODO: Check the logic
        return this.students.filter((student) => student.getFailedLessons().length !== 0);
    }
    getFailingManyLessons() {
        return this.students.filter((student) => student.getFailedLessons().length !== 0);
    }
    // TODO: Check if these methods should be in the client
    getAll() {
        return this.students;
    }
    getProgressions() {
        if (this.students.length === 0)
            return 0;
        const progressionsAdded = this.students.map((student) => student.getProgressions()).reduce((accumulative, current) => accumulative + current);
        return (progressionsAdded / this.students.length);
    }
    getWeekLearningTime() {
        if (this.students.length === 0)
            return 0;
        const learningTimesAdded = this.students.map((student) => student.getWeekLearningTime()).reduce((accumulative, current) => accumulative + current);
        return (learningTimesAdded / this.students.length);
    }
    getWeekProgressions() {
        if (this.students.length === 0)
            return 0;
        const progressionAdded = this.students.map((student) => student.getWeekProgression()).reduce((accumulative, current) => accumulative + current);
        return (progressionAdded / this.students.length);
    }
    getMathsAge() {
        if (this.students.length === 0) {
            return 0;
        }
        const mathsAgesAdded = this.students.map((student) => student.getMathsAge()).reduce((accumulative, current) => accumulative + current);
        return (mathsAgesAdded / this.students.length);
    }
    getWeeksMathsDelta() {
        if (this.students.length === 0)
            return 0;
        const mathsAgesAdded = this.students.map((student) => student.getWeeksMathsAgeDelta()).reduce((accumulative, current) => accumulative + current);
        return (mathsAgesAdded / this.students.length);
    }
    getWeeksLearningTime() {
        if (this.students.length === 0)
            return 0;
        const learningTimesAdded = this.students.map((student) => student.getWeeksLearningTime()).reduce((accumulative, current) => accumulative + current);
        return (learningTimesAdded / this.students.length);
    }
    getWeeksProgressions() {
        if (this.students.length === 0)
            return 0;
        const progressionAdded = this.students.map((student) => student.getWeeksProgression()).reduce((accumulative, current) => accumulative + current);
        return (progressionAdded / this.students.length);
    }
    getStartDate() {
        return this.startDate;
    }
    setStartDate(startDate) {
        this.startDate = DateTime.isDateTime(startDate) ? startDate : DateTime.fromISO(startDate);
        return this;
    }
    getEndDate() {
        return this.endDate;
    }
    setEndDate(endDate) {
        if (null != endDate) {
            this.endDate = DateTime.isDateTime(endDate) ? endDate : DateTime.fromISO(endDate);
        }
        else {
            this.endDate = DateTime.now();
        }
        return this;
    }
    getRecentDate() {
        return this.recentDate;
    }
    setRecentDate(recentDate) {
        if (null != recentDate) {
            this.recentDate = DateTime.isDateTime(recentDate) ? recentDate : DateTime.fromISO(recentDate);
        }
        else {
            this.recentDate = DateTime.now().startOf('week');
        }
        return this;
    }
    getStudents() {
        return this.students;
    }
    setStudents(students) {
        this.students = students;
        return this;
    }
}

class Group extends GuardRepositoryModel {
    constructor({ id, }) {
        super();
        this.id = id;
    }
    getId() {
        return this.id;
    }
    getStudents() {
        // TODO Implementation
        return [];
    }
}

class NewClass extends Group {
    constructor({ id, name, school_id, schoolyeargroupid, schoolyeargroup_name, }) {
        super({
            id,
        });
        this.name = name;
        this.school_id = school_id;
        this.schoolyeargroupid = schoolyeargroupid;
        this.schoolyeargroup_name = schoolyeargroup_name;
    }
    getName() {
        return this.name;
    }
    getStudents() {
        return super.getStudents();
    }
    getSchoolId() {
        return this.school_id;
    }
    getSchoolYearGroupId() {
        return this.schoolyeargroupid;
    }
    getSchoolYearGroupName() {
        return this.schoolyeargroup_name;
    }
}

class NewTeacher extends GuardRepositoryModel {
    constructor({ chukModel, locale_alternatives, locale_preferred, }) {
        super();
        this.chukModel = new Teacher(chukModel);
        this.locale_alternatives = locale_alternatives
            ? locale_alternatives.map((locale) => new Locale(locale))
            : [];
        if (locale_preferred) {
            this.locale_preferred = locale_preferred instanceof Locale ? locale_preferred : new Locale(locale_preferred);
        }
        else {
            this.locale_preferred = new Locale();
        }
    }
    getChukModel() {
        return this.chukModel;
    }
    setChukModel(teacherModel) {
        this.chukModel = new Teacher(teacherModel);
        return this;
    }
    getLocaleAlternatives() {
        return this.locale_alternatives;
    }
    setLocaleAlternatives(locale_alternatives) {
        this.locale_alternatives = locale_alternatives
            ? locale_alternatives.map((locale) => new Locale(locale))
            : [];
        return this;
    }
    getLocalePreferred() {
        return this.locale_preferred;
    }
    setLocalePreferred(locale_preferred) {
        if (locale_preferred) {
            this.locale_preferred = locale_preferred instanceof Locale ? locale_preferred : new Locale(locale_preferred);
        }
        else {
            this.locale_preferred = new Locale();
        }
        return this;
    }
    getClasses() {
        // TODO Implementation
        return [];
    }
    getWeekStartDate() {
        // TODO Implementation
        return null;
    }
}

var LessonStatusEnum;
(function (LessonStatusEnum) {
    LessonStatusEnum["INCOMPLETE"] = "Incomplete";
    LessonStatusEnum["REGRESSED"] = "Regressed";
    LessonStatusEnum["STATIC"] = "Static";
    LessonStatusEnum["PASSED"] = "Passed";
    LessonStatusEnum["JUMPED"] = "Jumped";
    LessonStatusEnum["EMPTY"] = "";
})(LessonStatusEnum || (LessonStatusEnum = {}));
var LessonStatusEnum$1 = LessonStatusEnum;

class LessonHistory extends GuardRepositoryModel {
    constructor({ topic, exercise, type, timeTaken, help, mark, ref, timestamp, locale, jumped, jumped_back, incomplete, }) {
        super();
        this.topic = topic;
        this.exercise = exercise;
        this.type = type;
        this.timeTaken = timeTaken;
        this.help = help;
        this.mark = mark;
        this.ref = ref;
        this.timestamp = DateTime.isDateTime(timestamp) ? timestamp : DateTime.fromISO(timestamp);
        this.locale = new Locale(locale);
        this.jumped = jumped;
        this.jumped_back = jumped_back;
        this.incomplete = incomplete;
    }
    getLocale() {
        return this.locale;
    }
    setLocale(value) {
        this.locale = new Locale(value);
        return this;
    }
    getTimestamp() {
        return this.timestamp;
    }
    setTimestamp(timestamp) {
        this.timestamp = DateTime.isDateTime(timestamp) ? timestamp : DateTime.fromISO(timestamp);
        return this;
    }
    getRef() {
        return this.ref;
    }
    setRef(value) {
        this.ref = value;
        return this;
    }
    getMark() {
        return this.mark;
    }
    setMark(value) {
        this.mark = value;
        return this;
    }
    getHelp() {
        return this.help;
    }
    setHelp(value) {
        this.help = value;
        return this;
    }
    getTimeTaken() {
        return this.timeTaken;
    }
    setTimeTaken(value) {
        this.timeTaken = value;
        return this;
    }
    getType() {
        return this.type;
    }
    setType(value) {
        this.type = value;
        return this;
    }
    getExercise() {
        return this.exercise;
    }
    setExercise(value) {
        this.exercise = value;
        return this;
    }
    getTopic() {
        return this.topic;
    }
    setTopic(value) {
        this.topic = value;
        return this;
    }
    getJumped() {
        return this.jumped;
    }
    getJumpedBack() {
        return this.jumped_back;
    }
    getStatus() {
        if ('Replay' === this.type)
            return LessonStatusEnum$1.EMPTY;
        if (this.incomplete)
            return LessonStatusEnum$1.INCOMPLETE;
        if (this.jumped)
            return LessonStatusEnum$1.JUMPED;
        if (this.jumped_back)
            return LessonStatusEnum$1.REGRESSED;
        const score = Number.parseInt(this.mark);
        if ('Exercise' === this.type) {
            if (score >= 30 && score < 70)
                return LessonStatusEnum$1.STATIC;
            if (score < 30)
                return LessonStatusEnum$1.REGRESSED;
        }
        if ('Test' === this.type) {
            if (score < 60)
                return LessonStatusEnum$1.STATIC;
        }
        return LessonStatusEnum$1.PASSED;
    }
}

class TopicReplayEntry extends GuardRepositoryModel {
    constructor({ active, currentTopicName, currentTutorLesson, firstname, gender, hasHomeSub, inAssessment, lastname, mathsAge, studentId, topicFocus, }) {
        super();
        this.active = active;
        this.currentTopicName = currentTopicName;
        this.currentTutorLesson = currentTutorLesson;
        this.firstname = firstname;
        this.gender = gender;
        this.hasHomeSub = hasHomeSub;
        this.inAssessment = inAssessment;
        this.lastname = lastname;
        this.mathsAge = mathsAge;
        this.studentId = studentId;
        const currentMapped = topicFocus.current ? Object.assign(Object.assign({}, topicFocus.current), { startDate: DateTime.isDateTime(topicFocus.current.startDate) ? topicFocus.current.startDate : DateTime.fromISO(topicFocus.current.startDate), endDate: DateTime.isDateTime(topicFocus.current.endDate) ? topicFocus.current.endDate : DateTime.fromISO(topicFocus.current.endDate) }) : null;
        const previousMapped = topicFocus.previous ? Object.assign(Object.assign({}, topicFocus.previous), { startDate: DateTime.isDateTime(topicFocus.previous.startDate) ? topicFocus.previous.startDate : DateTime.fromISO(topicFocus.previous.startDate), endDate: DateTime.isDateTime(topicFocus.previous.endDate) ? topicFocus.previous.endDate : DateTime.fromISO(topicFocus.previous.endDate) }) : null;
        this.topicFocus = {
            current: currentMapped,
            previous: previousMapped,
        };
    }
    getActive() {
        return this.active;
    }
    getCurrentTopicName() {
        return this.currentTopicName;
    }
    getCurrentTutorLesson() {
        return this.currentTutorLesson;
    }
    getFirstname() {
        return this.firstname;
    }
    getGender() {
        return this.gender;
    }
    getHasHomeSub() {
        return this.hasHomeSub;
    }
    getInAssessment() {
        return this.inAssessment;
    }
    getLastname() {
        return this.lastname;
    }
    getMathsAge() {
        return this.mathsAge;
    }
    getStudentId() {
        return this.studentId;
    }
    getTopicFocus() {
        return this.topicFocus;
    }
    isAvailableForTopicFocus() {
        return null != this.getTopicFocus().current || this.getInAssessment() || this.getHasHomeSub();
    }
    getFullName() {
        return `${TopicReplayEntry.ucWord(this.firstname)}${TopicReplayEntry.ucWord(this.lastname ? ' ' + this.lastname : '')}`;
    }
    static ucWord(word) {
        return `${word.substr(0, 1).toUpperCase()}${word.substr(1)}`;
    }
}

class GroupTopicProgress extends GuardRepositoryModel {
    constructor({ topics, students }) {
        super();
        this.topics = topics;
        this.students = students;
    }
    getTopics() {
        return this.topics;
    }
    setTopic(topics) {
        this.topics = topics;
        return this;
    }
    getStudents() {
        return this.students;
    }
    setStudents(students) {
        this.students = students;
        return this;
    }
}

class TopicStudentsEntry extends GuardRepositoryModel {
    constructor({ topic_id, topic_name, topic_start_age, topic_end_age, completed, covering, not_ready, }) {
        super();
        this.topic_id = topic_id;
        this.topic_name = topic_name;
        this.topic_start_age = topic_start_age;
        this.topic_end_age = topic_end_age;
        this.completed = completed !== null && completed !== void 0 ? completed : [];
        this.covering = covering !== null && covering !== void 0 ? covering : [];
        this.not_ready = not_ready !== null && not_ready !== void 0 ? not_ready : [];
    }
    getTopicId() {
        return this.topic_id;
    }
    getTopicName() {
        return this.topic_name;
    }
    getTopicStartDate() {
        return this.topic_start_age;
    }
    getEndAge() {
        return this.topic_end_age;
    }
    getCompleted() {
        return this.completed;
    }
    getCovering() {
        return this.covering;
    }
    getNotReady() {
        return this.not_ready;
    }
}

class StudentTopicFocus extends GuardRepositoryModel {
    constructor({ studentId, topicId, durationProgression, durationStartDate, durationEndDate }) {
        super();
        this.studentId = StudentTopicFocus.castToInteger(studentId);
        this.topicId = StudentTopicFocus.castToString(topicId);
        this.durationProgression = StudentTopicFocus.castToInteger(durationProgression);
        this.durationStartDate = StudentTopicFocus.castToDate(durationStartDate);
        this.durationEndDate = StudentTopicFocus.castToDate(durationEndDate);
    }
    getStudentId() {
        return this.studentId;
    }
    setStudentId(studentId) {
        this.studentId = studentId;
        return this;
    }
    getTopicId() {
        return this.topicId;
    }
    setTopicId(topicId) {
        this.topicId = topicId;
        return this;
    }
    getDurationProgression() {
        return this.durationProgression;
    }
    setDurationProgression(durationProgression) {
        this.durationProgression = durationProgression;
        return this;
    }
    getDurationStartDate() {
        return this.durationStartDate;
    }
    setDurationStartDate(durationStartDate) {
        this.durationStartDate = StudentTopicFocus.castToDate(durationStartDate);
        return this;
    }
    getDurationEndDate() {
        return this.durationEndDate;
    }
    setDurationEndDate(durationEndDate) {
        this.durationEndDate = StudentTopicFocus.castToDate(durationEndDate);
        return this;
    }
}

class NewStudentTutorDataMetrics extends NewStudentWithTags {
    constructor({ first_name, id, last_name, username, tags, mathsAge, mathsAgeImprovement, progressions, learningTime, consistency, lastAssessmentDate, firstAssessmentDate, timeSpan, mathsAgeAtStart, tutorUsage, topicChallengeUsage, progressionPassRate, exerciseMark, testMark, actualAge, minutesPerProgression, parentLink, progressionsPerWeek, }) {
        super({
            first_name,
            id,
            last_name,
            username,
            tags,
        });
        this.mathsAge = mathsAge;
        this.mathsAgeImprovement = mathsAgeImprovement;
        this.progressions = progressions;
        this.learningTime = learningTime;
        this.consistency = consistency;
        this.lastAssessmentDate = lastAssessmentDate
            ? DateTime.isDateTime(lastAssessmentDate) ? lastAssessmentDate : DateTime.fromISO(lastAssessmentDate)
            : null;
        this.firstAssessmentDate = firstAssessmentDate
            ? DateTime.isDateTime(firstAssessmentDate) ? firstAssessmentDate : DateTime.fromISO(firstAssessmentDate)
            : null;
        this.timeSpan = timeSpan;
        this.mathsAgeAtStart = mathsAgeAtStart;
        this.tutorUsage = tutorUsage;
        this.topicChallengeUsage = topicChallengeUsage;
        this.progressionPassRate = progressionPassRate;
        this.exerciseMark = exerciseMark;
        this.testMark = testMark;
        this.actualAge = actualAge;
        this.minutesPerProgression = minutesPerProgression;
        this.parentLink = parentLink;
        this.progressionsPerWeek = progressionsPerWeek;
    }
    getMathsAge() {
        return this.mathsAge;
    }
    getMathsAgeImprovement() {
        return this.mathsAgeImprovement;
    }
    getProgressions() {
        return this.progressions;
    }
    getLearningTime() {
        return this.learningTime;
    }
    getConsistency() {
        return this.consistency;
    }
    getLastAssessment() {
        return this.lastAssessmentDate;
    }
    getFirstAssessment() {
        return this.firstAssessmentDate;
    }
    getTimeSpan() {
        return this.timeSpan;
    }
    getMathsAgeAtStart() {
        return this.mathsAgeAtStart;
    }
    getTutorUsage() {
        return this.tutorUsage;
    }
    getTopicChallengeUsage() {
        return this.topicChallengeUsage;
    }
    getProgressionPassRate() {
        return this.progressionPassRate;
    }
    getExerciseMark() {
        return this.exerciseMark;
    }
    getTestMark() {
        return this.testMark;
    }
    getActualAge() {
        return this.actualAge;
    }
    getMinutesPerProgression() {
        return this.minutesPerProgression;
    }
    getParentLink() {
        return this.parentLink;
    }
    getProgressionsPerWeek() {
        return this.progressionsPerWeek;
    }
    getConsistencyName() {
        // TODO: check actual conditions
        return 'constant';
    }
    getTickStatus() {
        const averageWeeklyProgressionsLessThan3 = (this.progressionsPerWeek < 3);
        const averageWeeklyUsageLessThan30 = (this.learningTime < 30);
        if (averageWeeklyProgressionsLessThan3 && !averageWeeklyUsageLessThan30)
            return 'amber';
        if (averageWeeklyProgressionsLessThan3 && averageWeeklyUsageLessThan30)
            return 'red';
        return 'green';
    }
    getStudentStatus() {
        // TODO: check actual conditions
        return 'superstar';
    }
    hasDataLast8Weeks() {
        if (this.firstAssessmentDate === null)
            return false;
        return this.firstAssessmentDate.toMillis() < DateTime.now().minus({ week: 8 }).toMillis();
    }
}

class GroupReportMetrics extends Group {
    constructor({ id, startDate, endDate, studentsMetrics, }) {
        super({ id });
        this.startDate = startDate;
        this.endDate = endDate;
        this.studentsMetrics = studentsMetrics.map((st) => new NewStudentTutorDataMetrics(st));
    }
    getStartDate() {
        return this.startDate;
    }
    getEndDate() {
        return this.endDate;
    }
    getStudentsMetrics() {
        return this.studentsMetrics;
    }
    getMathsAgeAverage() {
        return this.studentsMetrics
            .map((student) => student.getMathsAge())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getMathsAgeImprovementAverage() {
        return this.studentsMetrics
            .map((student) => student.getMathsAgeImprovement())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getProgressionsAverage() {
        return this.studentsMetrics
            .map((student) => student.getProgressions())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getLearningTimeAverage() {
        return this.studentsMetrics
            .map((student) => student.getLearningTime())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getConsistencyAverage() {
        return this.studentsMetrics
            .map((student) => student.getConsistency())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getTimeSpanAverage() {
        return this.studentsMetrics
            .map((student) => student.getTimeSpan())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getMathsAgeAtStartAverage() {
        return this.studentsMetrics
            .map((student) => student.getMathsAgeAtStart())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getTutorUsageAverage() {
        return this.studentsMetrics
            .map((student) => student.getTutorUsage())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getTopicChallengeUsageAverage() {
        return this.studentsMetrics
            .map((student) => student.getTopicChallengeUsage())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getProgressionPassRateAverage() {
        return this.studentsMetrics
            .map((student) => student.getProgressionPassRate())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getExerciseMarkAverage() {
        return this.studentsMetrics
            .map((student) => student.getExerciseMark())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getTestMarkAverage() {
        return this.studentsMetrics
            .map((student) => student.getTestMark())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getActualAgeAverage() {
        return this.studentsMetrics
            .map((student) => student.getActualAge())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getMinutesPerProgressionAverage() {
        return this.studentsMetrics
            .map((student) => student.getMinutesPerProgression())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getProgressionsPerWeekAverage() {
        return this.studentsMetrics
            .map((student) => student.getProgressionsPerWeek())
            .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / this.studentsMetrics.length;
    }
    getConsistencyNameAverage() {
        // TODO: check actual conditions
        return 'constant';
    }
    getTickStatusAverage() {
        const averageWeeklyProgressionsLessThan3 = (this.getProgressionsPerWeekAverage() < 3);
        const averageWeeklyUsageLessThan30 = (this.getLearningTimeAverage() < 30);
        if (averageWeeklyProgressionsLessThan3 && !averageWeeklyUsageLessThan30)
            return 'amber';
        if (averageWeeklyProgressionsLessThan3 && averageWeeklyUsageLessThan30)
            return 'red';
        return 'green';
    }
    getStudentStatusAverage() {
        // TODO: check actual conditions
        return 'superstar';
    }
}

var AuthenticatorEnum;
(function (AuthenticatorEnum) {
    AuthenticatorEnum["AUTHTOKEN"] = "authtoken";
    AuthenticatorEnum["CLEVER"] = "clever";
    AuthenticatorEnum["FACEBOOK"] = "facebook";
    AuthenticatorEnum["GOOGLE"] = "google";
    AuthenticatorEnum["LIVECIRRUS"] = "livecirrus";
    AuthenticatorEnum["SSO"] = "sso";
    AuthenticatorEnum["STANDARD"] = "standard";
})(AuthenticatorEnum || (AuthenticatorEnum = {}));
var AuthenticatorEnum$1 = AuthenticatorEnum;

var ConsistencyEnum;
(function (ConsistencyEnum) {
    ConsistencyEnum["CONSTANT"] = "constant";
    ConsistencyEnum["INTERMITTENT"] = "intermittent";
    ConsistencyEnum["OCCASIONAL"] = "occasional";
    ConsistencyEnum["RELIABLE"] = "reliable";
    ConsistencyEnum["STEADY"] = "steady";
})(ConsistencyEnum || (ConsistencyEnum = {}));
var ConsistencyEnum$1 = ConsistencyEnum;

var GroupReportFiltersEnum;
(function (GroupReportFiltersEnum) {
    GroupReportFiltersEnum["ASSESSED_NOT_BEING_REASSESSED"] = "assessedNotBeingReassessed";
    GroupReportFiltersEnum["NOT_ASSESSED_OR_REASSESSED"] = "notAssessedOrReassessed";
    GroupReportFiltersEnum["HAVE_NEVER_BEEN_ASSESSED"] = "haveNeverBeenAssessed";
    GroupReportFiltersEnum["ASSESSED_OR_BEING_REASSESSED"] = "assessedOrBeingReassessed";
})(GroupReportFiltersEnum || (GroupReportFiltersEnum = {}));
var GroupReportFiltersEnum$1 = GroupReportFiltersEnum;

var LessonOutcomeEnum;
(function (LessonOutcomeEnum) {
    LessonOutcomeEnum["FAILED"] = "failed";
    LessonOutcomeEnum["PASSED"] = "passed";
    LessonOutcomeEnum["STATIC"] = "static";
})(LessonOutcomeEnum || (LessonOutcomeEnum = {}));
var LessonOutcomeEnum$1 = LessonOutcomeEnum;

var LessonTypeEnum;
(function (LessonTypeEnum) {
    LessonTypeEnum["EXERCISE"] = "Exercise";
    LessonTypeEnum["TEST"] = "Test";
})(LessonTypeEnum || (LessonTypeEnum = {}));
var LessonTypeEnum$1 = LessonTypeEnum;

var MedalEnum;
(function (MedalEnum) {
    MedalEnum["GOLD"] = "gold";
    MedalEnum["SILVER"] = "silver";
    MedalEnum["BRONZE"] = "bronze";
})(MedalEnum || (MedalEnum = {}));
var MedalEnum$1 = MedalEnum;

var NameTypeEnum;
(function (NameTypeEnum) {
    NameTypeEnum["FULL"] = "FULL";
    NameTypeEnum["FIRST"] = "FIRST";
    NameTypeEnum["LAST"] = "LAST";
})(NameTypeEnum || (NameTypeEnum = {}));
var NameTypeEnum$1 = NameTypeEnum;

var StatusEnum;
(function (StatusEnum) {
    StatusEnum["ACCEPTED"] = "accepted";
    StatusEnum["PENDING"] = "pending";
    StatusEnum["REJECTED"] = "rejected";
    StatusEnum["COMPLETED"] = "completed";
})(StatusEnum || (StatusEnum = {}));
var StatusEnum$1 = StatusEnum;

var StudentStatusEnum;
(function (StudentStatusEnum) {
    StudentStatusEnum["ROOKIE"] = "rookie";
    StudentStatusEnum["PRO"] = "pro";
    StudentStatusEnum["SUPERSTAR"] = "superstar";
    StudentStatusEnum["APPRENTICE"] = "apprentice";
    StudentStatusEnum["MASTER"] = "master";
})(StudentStatusEnum || (StudentStatusEnum = {}));
var StudentStatusEnum$1 = StudentStatusEnum;

var TickStatusEnum;
(function (TickStatusEnum) {
    TickStatusEnum["GREEN"] = "green";
    TickStatusEnum["AMBER"] = "amber";
    TickStatusEnum["RED"] = "red";
})(TickStatusEnum || (TickStatusEnum = {}));
var TickStatusEnum$1 = TickStatusEnum;

var TimeAssessmentFilterEnum;
(function (TimeAssessmentFilterEnum) {
    TimeAssessmentFilterEnum["FIRST_ASSESSMENT"] = "first-assessment";
    TimeAssessmentFilterEnum["LAST_ASSESSMENT"] = "last-assessment";
})(TimeAssessmentFilterEnum || (TimeAssessmentFilterEnum = {}));
var TimeAssessmentFilterEnum$1 = TimeAssessmentFilterEnum;

var TopicIdEnum;
(function (TopicIdEnum) {
    TopicIdEnum["AA"] = "AA";
    TopicIdEnum["BA"] = "BA";
    TopicIdEnum["CA"] = "CA";
    TopicIdEnum["DA"] = "DA";
    TopicIdEnum["EA"] = "EA";
    TopicIdEnum["FA"] = "FA";
    TopicIdEnum["GA"] = "GA";
    TopicIdEnum["HA"] = "HA";
    TopicIdEnum["JA"] = "JA";
    TopicIdEnum["KA"] = "KA";
    TopicIdEnum["LA"] = "LA";
    TopicIdEnum["MA"] = "MA";
    TopicIdEnum["NA"] = "NA";
    TopicIdEnum["PA"] = "PA";
    TopicIdEnum["QA"] = "QA";
    TopicIdEnum["RA"] = "RA";
    TopicIdEnum["SA"] = "SA";
    TopicIdEnum["TA"] = "TA";
    TopicIdEnum["UA"] = "UA";
    TopicIdEnum["VA"] = "VA";
    TopicIdEnum["WA"] = "WA";
    TopicIdEnum["ZA"] = "ZA";
})(TopicIdEnum || (TopicIdEnum = {}));
var TopicIdEnum$1 = TopicIdEnum;

var UsagePropertyEnum;
(function (UsagePropertyEnum) {
    UsagePropertyEnum["REPLAY"] = "replay";
    UsagePropertyEnum["TUTOR"] = "tutor";
    UsagePropertyEnum["TOTAL"] = "total";
    UsagePropertyEnum["ASSESSMENT"] = "assessment";
})(UsagePropertyEnum || (UsagePropertyEnum = {}));
var UsagePropertyEnum$1 = UsagePropertyEnum;

var UserTypeEnum;
(function (UserTypeEnum) {
    UserTypeEnum["ORGANIZATION_CONTACT"] = "organization_contact";
    UserTypeEnum["PARENT"] = "parent";
    UserTypeEnum["STUDENT"] = "student";
    UserTypeEnum["TEACHER"] = "teacher";
})(UserTypeEnum || (UserTypeEnum = {}));
var UserTypeEnum$1 = UserTypeEnum;

var RosterManagementStudentActionEnum;
(function (RosterManagementStudentActionEnum) {
    RosterManagementStudentActionEnum["edit"] = "edit";
    RosterManagementStudentActionEnum["view"] = "view";
    RosterManagementStudentActionEnum["moveToClass"] = "moveToClass";
    RosterManagementStudentActionEnum["addToExtraClass"] = "addToExtraClass";
    RosterManagementStudentActionEnum["removeFromClass"] = "removeFromClass";
    RosterManagementStudentActionEnum["moveToYearGroup"] = "moveToYearGroup";
    RosterManagementStudentActionEnum["activate"] = "activate";
    RosterManagementStudentActionEnum["deactivate"] = "deactivate";
    RosterManagementStudentActionEnum["archive"] = "archive";
    RosterManagementStudentActionEnum["unarchive"] = "unarchive";
    RosterManagementStudentActionEnum["addTags"] = "addTags";
    RosterManagementStudentActionEnum["removeTags"] = "removeTags";
    RosterManagementStudentActionEnum["setUsageCap"] = "setUsageCap";
    RosterManagementStudentActionEnum["completelyDeleteArchived"] = "completelyDeleteArchived";
})(RosterManagementStudentActionEnum || (RosterManagementStudentActionEnum = {}));
var RosterManagementStudentActionEnum$1 = RosterManagementStudentActionEnum;

var EspFileStatusEnum;
(function (EspFileStatusEnum) {
    EspFileStatusEnum["VALIDATING"] = "validating";
    EspFileStatusEnum["PROCESSING"] = "processing";
    EspFileStatusEnum["SUCCESS"] = "success";
    EspFileStatusEnum["ERROR"] = "error";
})(EspFileStatusEnum || (EspFileStatusEnum = {}));
var EspFileStatusEnum$1 = EspFileStatusEnum;

var JobTypeEnum;
(function (JobTypeEnum) {
    JobTypeEnum[JobTypeEnum["TEACHER"] = 0] = "TEACHER";
    JobTypeEnum[JobTypeEnum["PRINCIPAL"] = 1] = "PRINCIPAL";
    JobTypeEnum[JobTypeEnum["DEPUTY"] = 2] = "DEPUTY";
    JobTypeEnum[JobTypeEnum["MATHS_LEAD"] = 3] = "MATHS_LEAD";
    JobTypeEnum[JobTypeEnum["SENIOR_LEADERSHIP"] = 4] = "SENIOR_LEADERSHIP";
    JobTypeEnum[JobTypeEnum["ASSISTANT"] = 5] = "ASSISTANT";
    JobTypeEnum[JobTypeEnum["FINANCE"] = 6] = "FINANCE";
    JobTypeEnum[JobTypeEnum["OTHER"] = 7] = "OTHER";
})(JobTypeEnum || (JobTypeEnum = {}));
var JobTypeEnum$1 = JobTypeEnum;

export { Assessment, AuthHistory, AuthStats, AuthenticatorEnum$1 as AuthenticatorEnum, Avatar, Buddy, BuddyLeaderboard, Challenge, ChallengeStats, ConsistencyEnum$1 as ConsistencyEnum, CreditCard, EspFileStatusEnum$1 as EspFileStatusEnum, Feature, Feedback, GlobalLeaderboard, Group, GroupReport, GroupReportFiltersEnum$1 as GroupReportFiltersEnum, GroupReportMetrics, GroupTopicProgress, Identity, Item, JobTypeEnum$1 as JobTypeEnum, Leaderboard, LeaderboardEntry, Lesson, LessonFeedback, LessonHistory, LessonLeaderboard, LessonLeaderboardEntry, LessonOutcomeEnum$1 as LessonOutcomeEnum, LessonResult, LessonStatusEnum$1 as LessonStatusEnum, LessonTypeEnum$1 as LessonTypeEnum, Locale, MedalEnum$1 as MedalEnum, Message, Model, NameTypeEnum$1 as NameTypeEnum, NewClass, NewLessonOutcome, NewStudentBasic, NewStudentTutorData, NewStudentTutorDataMetrics, NewStudentWithTags, NewTeacher, OrganizationContact, Parent, PaymentMethod, Preference, ReportModel, RosterManagementStudentActionEnum$1 as RosterManagementStudentActionEnum, School, SchoolLeaderboard, SiblingDiscount, StatusEnum$1 as StatusEnum, Sticker, Student, StudentMathsAgeReport, StudentProgressionReport as StudentProgressionsReport, StudentReportModel, StudentStatusEnum$1 as StudentStatusEnum, StudentTopicFocus, StudentTopicsReport, StudentUsageReport, Subscription, SubscriptionPlan, Teacher, TickStatusEnum$1 as TickStatusEnum, TimeAssessmentFilterEnum$1 as TimeAssessmentFilterEnum, TimeTaken, Topic, TopicIdEnum$1 as TopicIdEnum, TopicProgress, TopicReplay, TopicReplayEntry, TopicStudentsEntry, Usage, UsagePropertyEnum$1 as UsagePropertyEnum, User, UserTypeEnum$1 as UserTypeEnum, WeeklySummary };
